<template>
    <div id="emap">
        <NavBar />

        <!-- 讀取訊息 -->
        <div v-show="is_show_loading_text" 
            class="msg-container"
        >
            <div class="msg-mask"></div>
            <div class="text-container">
                <v-progress-circular
                    indeterminate
                    size="18"
                    width="2"
                    color="white"
                />
                <span class="text">
                    {{ $t('__emap.loading_text') }}
                </span>
            </div>
        </div>

        <!-- 目前無資料或無發布訊息 -->
        <div v-show="is_show_empty_text" 
            class="msg-container empty"
        >
            <div class="text-container">
                <p 
                    v-for="(product, index) in empty_alert_list"
                    :key="index"
                    class="text"
                >
                    {{ product.pane_text }}
                </p>
                <p class="text">
                    {{ $t('__product_setting.no_data_and_no_release') }}
                </p>
            </div>
        </div>

        <!-- 我的最愛提示文字 -->
        <FavoritesHint 
            class="favorites-hint" 
            v-if="is_show_favorites_hint" 
            :favorites_hint_text="favorites_hint_text"
        />

        <KXDMap 
            v-if="Object.keys(kmap).length > 0"
            :kmap="kmap"
            :maptool_colorbars_list="maptool_colorbars_list"
            :maptool_legends_list="maptool_legends_list"
            :max_rain="is_show_unique_product_feature.max_rain"
            :is_show_popup_table="is_show_popup_table"
            :is_show_favorites="is_show_favorites"
            ref="kxdmap"
            @transfer-panes-config="assign_panes_config"
            @change_favorites_hint_text="change_favorites_hint_text"
        />

        <!-- 雷達回波3D -->
        <VerticalRadar v-if="is_show_unique_product_feature.vertical_product"
            :vertical_product="is_show_unique_product_feature.vertical_product"
            ref="vertical_radar"
        />

        <div class="legends-content__wrapper">
            <!-- geojson monitor -->
            <div v-if="is_loaded_monitor_message"
                v-show="is_show_unique_product_feature.monitor"
                id="monitor_message"
            >
                <template v-if="check_in_maptool_legends_list('monitor')">
                    <div class="card">
                        <div class="card-content white--text">
                            監控雨量<br>
                            預警值：<span class="monitor_message--yellow">●</span>
                            警戒值：<span class="monitor_message--orange">●</span>
                            行動值：<span class="monitor_message--red">●</span>
                        </div>
                    </div>
                </template>
            </div> 

            <!-- geojson lightning -->
            <div
                v-if="is_loaded_lightning_message"
                v-show="is_show_unique_product_feature.lightning"
                id="lightning-message"
            >
                <div v-if="check_in_maptool_legends_list('lightning')" 
                    class="card"
                >
                    <div class="card-content white--text">
                        <p><img src="/static/images/product/lightning/3minCloudToGround.png">{{ $t('__geojson.CG_last_3_mins') }}</p>
                        <p><img src="/static/images/product/lightning/3minCloudToCloud.png">{{ $t('__geojson.IC_last_3_mins') }}</p>
                        <p><img src="/static/images/product/lightning/10minCloudToGround.png">{{ $t('__geojson.CG_last_10_mins') }}</p>
                        <p><img src="/static/images/product/lightning/10minCloudToCloud.png">{{ $t('__geojson.IC_last_10_mins') }}</p>
                        <p><img src="/static/images/product/lightning/30minCloudToGround.png">{{ $t('__geojson.CG_last_30_mins') }}</p>
                        <p><img src="/static/images/product/lightning/30minCloudToCloud.png">{{ $t('__geojson.IC_last_30_mins') }}</p>
                    </div>
                </div>
            </div> 

            <!-- geojson typhoon -->
            <div
                v-if="is_loaded_typhoon_message"
                v-show="is_show_unique_product_feature.ty_track"
                id="typhoon-message"
            >
                <div v-if="check_in_maptool_legends_list('typhoon')" 
                    class="card"
                >
                    <div class="card-content white--text">
                        <p>
                            <v-icon color="#ff0000"
                                size="16"
                            >
                                icon-moderate-typhoon
                            </v-icon>{{ $t('__geojson.typhoon_icon_text_severe') }}
                        </p>
                        <p>
                            <v-icon color="#22f7ff"
                                size="16"
                            >
                                icon-moderate-typhoon
                            </v-icon>{{ $t('__geojson.typhoon_icon_text_moderate') }}
                        </p>
                        <p>
                            <v-icon color="#e181f7"
                                size="16"
                            >
                                icon-mild-typhoon
                            </v-icon>{{ $t('__geojson.typhoon_icon_text_mild') }}
                        </p>
                        <p>
                            <v-icon color="#415ed3"
                                size="16"
                            >
                                icon-typhoon
                            </v-icon>{{ $t('__geojson.typhoon_icon_text_TD') }}
                        </p>
                    </div>
                </div>
            </div> 

            <!-- geomap freeway -->
            <div
                v-show="is_show_unique_product_feature.traffic_live"
                id="traffic-live"
            >
                <div v-show="check_in_maptool_legends_list('traffic_live')"
                    class="card" 
                >
                    <div class="card-content white--text">
                        即時路況<br>
                        順暢  ( >80km ): <span class="line level_1" /><br>
                        車多  (60~79km): <span class="line level_2" /><br>
                        車多  (40~59km): <span class="line level_3" /><br>
                        壅塞  (20~39km): <span class="line level_4" /><br>
                        嚴重壅塞(0~19km): <span class="line level_5" /><br>
                        道路封閉: <span class="line level_-1" /><br>
                        未取得資訊: <span class="line level_no-data" /><br>
                        <span v-show="traffic_live_display_time">
                            最後更新時間: {{ traffic_live_display_time }}
                        </span>
                    </div>
                </div>
            </div>
            <!-- gis station|auto -->
            <div 
                v-if="is_loaded_station_message"
                v-show="is_show_unique_product_feature.station"
                id="station_message"
            >
                <div v-show="check_in_maptool_legends_list('station')"
                    class="card" 
                >
                    <div class="card-content white--text">
                        {{ $t('__gis.station') }}<br>
                        <div class="legend">
                            <div class="legend-item"><span class="dot" style="background:#2c7da8"></span><span>：{{ $t('__gis.beaufort_scale') }} 0</span></div>
                            <div class="legend-item"><span class="dot" style="background:#2c8ca8"></span><span>：{{ $t('__gis.beaufort_scale') }} 1</span></div>
                            <div class="legend-item"><span class="dot" style="background:#2ca8a8"></span><span>：{{ $t('__gis.beaufort_scale') }} 2</span></div>
                            <div class="legend-item"><span class="dot" style="background:#41ab5d"></span><span>：{{ $t('__gis.beaufort_scale') }} 3</span></div>
                            <div class="legend-item"><span class="dot" style="background:#74c476"></span><span>：{{ $t('__gis.beaufort_scale') }} 4</span></div>
                            <div class="legend-item"><span class="dot" style="background:#b1edc1"></span><span>：{{ $t('__gis.beaufort_scale') }} 5</span></div>
                            <div class="legend-item"><span class="dot" style="background:#c7e9b4"></span><span>：{{ $t('__gis.beaufort_scale') }} 6</span></div>
                            <div class="legend-item"><span class="dot" style="background:#a50021"></span><span>：{{ $t('__gis.beaufort_scale') }} 7</span></div>
                            <div class="legend-item"><span class="dot" style="background:#cc0000"></span><span>：{{ $t('__gis.beaufort_scale') }} 8</span></div>
                            <div class="legend-item"><span class="dot" style="background:#ff4343"></span><span>：{{ $t('__gis.beaufort_scale') }} 9</span></div>
                            <div class="legend-item"><span class="dot" style="background:#c800ff"></span><span>：{{ $t('__gis.beaufort_scale') }} 10</span></div>
                            <div class="legend-item"><span class="dot" style="background:#df63d9"></span><span>：{{ $t('__gis.beaufort_scale') }} 11</span></div>
                            <div class="legend-item"><span class="dot" style="background:#fdafff"></span><span>：{{ $t('__gis.beaufort_scale') }} 12</span></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- gis gauge -->
            <div 
                v-if="is_loaded_gauge_message"
                v-show="is_show_unique_product_feature.gauge"
                id="gauge_message"
            >
                <div v-show="check_in_maptool_legends_list('gauge')"
                    class="card" 
                >
                    <div class="card-content white--text">
                        {{ $t('__gis.auto_gauge_station') }}<br>
                        <span class="level_1">●</span> ： {{ $t('__gis.not_exceeding_heavy_rain') }}<br>
                        <span class="level_2">●</span> ： {{ $t('__gis.heavy_rain') }}<br>
                        <span class="level_3">●</span> ： {{ $t('__gis.extremely_heavy_rain') }}<br>
                        <span class="level_4">●</span> ： {{ $t('__gis.torrential_rain') }}<br>
                        <span class="level_5">●</span> ： {{ $t('__gis.extremely_torrential_rain') }}<br>
                    </div>
                </div>
            </div>
        </div>

        <!-- 左下三個點點的設定按鈕 -->
        <LegendColorBarSetting
            v-show="is_show_maptool"
            v-bind="show_map_setting_options"
            @set_maptool_list="set_maptool_list"
        />

        <div class="bottom-dialog">
            <!-- geojson alert，特報與即時訊息不與 popup table 一起 -->
            <QpeAlertTable 
                v-if="Object.keys(need_display_alert_table_content).length !== 0"
                v-show="is_show_alert_table && !is_show_popup_table"
                :props_alerts="need_display_alert_table_content"
            />

            <!-- gis popup -->
            <!-- geojson description -->
            <PopupTable v-if="is_show_popup_table"
                :gis_popup_data="gis_popup_data"
                :gis_popup_type="gis_popup_type"
                :popup_type="popup_type"
                :description_info="description_info"
                @close-geojson-popup-table="clear_geojson_popup_data"
                @close-gis-popup-table="clear_gis_popup_data"
                @show-gis-diagram="show_wmarker_chart_dialog"
            />

            
            <div class="diagram-dialog" 
                :style="css_props"
            >
                <!-- gis chart -->
                <GisChart
                    v-if="Object.keys(chart_options).length"
                    id="belongs_emap_chart"
                    :chart_options="chart_options"
                    :default_variable="$t('__rainfall.rainfall')"
                    @close-chart-dialog="close_chart_dialog"
                />

                <SkewChart 
                    v-if="Object.keys(skew_options).length"
                    :skew_options="skew_options"
                    @close-chart-dialog="close_chart_dialog"
                />

                <!-- airplane_wind 時序圖 -->
                <AirplaneWindChart
                    v-if="Object.keys(airplane_wind_chart_options).length"
                    :chart_options="airplane_wind_chart_options"
                    @close-chart-dialog="close_chart_dialog"
                ></AirplaneWindChart>
            </div>
        </div>

        <div class="bottom-tool">
            <QpeTimeLine v-if="timebar_option.list.length > 0"
                :props_play_index.sync="timebar_option.props_play_index"
                :list="timebar_option.list"
                :duration="timebar_option.duration"
                :latest_complete_data_timestamp="timebar_option.latest_complete_data_timestamp"
                :is_show_loading_text="is_show_loading_text"
                @trigger-change-time="change_time"
            />
        </div>

        <img
            v-show="Object.keys(gis_popup_data).length"
            class="point-tooltip"
            src="/static/images/product/gis/station.png"
            :style="gis_icon_style"
        >

        <div
            v-show="focal_sign_number"
            class="table-sign point-tooltip"
            :style="table_sign.style"
        >
            {{ focal_sign_number }}
            <v-icon v-if="table_sign.icon_class"
                :class="[ table_sign.icon_class ]"
            >
                {{ table_sign.signal_class }}
            </v-icon>
            <span v-else
                class="table-sign__text"
            >
                -
            </span>
        </div>
        <AccumulatedPrecp
            v-show="is_show_accumulated_precp_table && !is_monitor_open_emap"
        />

        <template v-if="is_show_forecast_tool">
        <!--颱風控制小工具-->
            <VueDragResize 
                content-class="typhoon-forecast-tooltip"
                drag-handle=".drag"
                :is-active="true"
                :is-draggable="true"
                :is-resizable="false"
                :w="200"
                :h="105"
                :x="typhoon_forecast_tooltip_initial_position.x"
                :y="typhoon_forecast_tooltip_initial_position.y"
            >
                <div class="typhoon-forecast-title drag">
                    預測路徑工具
                    <v-icon
                        class="drag"
                        color="#b3b7c4" 
                    >
                        icon-move
                    </v-icon>
                </div>
                <div class="typhoon-forecast-control">
                    <v-select
                        v-model="typhoon_loop_duration"
                        :items="typhoon_spd_select"
                        :id="'typhoon-spd-select'"
                        :class="'typhoon-spd-select white--text'"
                        :label="`播放速度`"
                        item-text="text"
                        item-value="value"
                        @change="update_typhoon_loop_duration"
                    ></v-select>  
                    <div class="d-flex">
                        <v-icon
                            color="white"
                            @click="set_typhoon_forecast_step('prev')"
                        >
                            mdi-rewind
                        </v-icon>
                        <div
                            ref="typhoon_forecast_btn"
                            class="typhoon-forecast-btn typhoon-forecast-btn-play"
                            :class="{ active: is_show_typhoon_animation }"
                            @click="toggle_typhoon_animation()"
                        />
                        <v-icon
                            color="white"
                            @click="set_typhoon_forecast_step('next')"
                        >
                            mdi-fast-forward
                        </v-icon>
                    </div>
                </div>
            </VueDragResize>
        </template>
    </div>
</template>

<script>
/* global L */  
// L: leaflet function name
    import VueDragResize from 'vue-drag-resize';

    import { 
        set_feature_style, 
        set_layer_popup_descriptions,
        _convert_marker_data_to_html,
        assign_description,
        set_click_event,
    } from "@/utils/emap/emap_common"
    import { 
        get_common_create_layer_options, 
        get_file_path,
        calculate_pane_opacity,
        create_bind_marker_data_by_config,
        create_alert_content_structure,
        get_icon_marker_options,
    } from "@/utils/emap/common_layer.js"
    import {
        init_mutation_observer,
        calc_gis_tooltip_style, 
        calc_focal_sign_tooltip_style,
    } from "@/utils/emap/common_point.js"
    import {
        get_common_create_render_type_options,
        generate_data_by_render_sub_type,
        FORMAT_URL_CALLBACK_MAPPING,
        get_product_with_current_radar_angle,
        generate_accumulated_precp_data,
        get_wind_bar_style_multi_drawer,
        get_symbol_wind_style_drawer,
        get_render_colorbar_val_callback
    } from "@/utils/emap/type_render.js"
    import { get_remote_file_ajax } from "@/utils/emap/emap_init.js"
    import {
        transform_max_rain_color_bar_image, 
    } from "@/utils/emap/sub_type/max_rain.js"
    import { 
        get_typhoon_empty_data_config,
        update_ty_animation_timer_structure,
        generate_forecast_node_class,
        make_typhoon_time_marker,
        make_typhoon_name_tag,
        show_or_hide_typhoon_time_display
    } from "@/utils/emap/sub_type/ty_track.js"
    import {
        emit_dropsonde_data
    } from "@/utils/emap/sub_type/dropsonde.js"
    import {
        record_point_latlng,
        bind_table_sign_tooltip
    } from "@/utils/emap/sub_type/table_sign.js"
    import { set_product_geomap_images_click_event } from '@/utils/emap/type_geomap.js'
    import { 
        emit_station_data
    } from '@/utils/emap/type_gis.js'
    import { get_live_traffic_data } from "@/utils/freeway/background.js"
    import { transform_datetime_to_string } from '@/utils/common/custom_time.js'

    import { MapConfig } from '@/config/map_config.js'
    import { MapConfigEN } from '@/config/map_config_en.js'
    import { MapConfigPro } from '@/config/map_config_pro.js'
    import { WMarkerConfig } from '@/config/wmarker_config'
    import { topline_config } from '@/config/topline_config'
    import { UNITS_PRO } from '@/config/config.js'
    import { mapGetters, mapMutations, mapState } from 'vuex'
    import intersection from 'lodash/intersection'
    import difference from 'lodash/difference'

    import NavBar from '@/components/navbar.vue'
    import KXDMap from '@kxd_map/map.vue'
    import QpeTimeLine from '@/components/emap/emap_custom_plugin/qpe_timeline.vue'
    import LegendColorBarSetting from '@/components/emap/legend_color_bar_tool.vue'

    export default {
        name: 'Emap',
        
        components: {
            NavBar,
            KXDMap, 
            QpeTimeLine,
            LegendColorBarSetting,
            VueDragResize,
            QpeAlertTable: () => import('@/components/emap/emap_custom_plugin/qpe_alert_table.vue'),
            PopupTable: () => import('@/components/emap/emap_custom_plugin/popup_table.vue'),
            GisChart: () => import('@/components/chart/gis_chart.vue'),
            AirplaneWindChart: () => import('@/components/chart/airplane_wind_chart.vue'),
            SkewChart: () => import('@/components/chart/dropsonde/skew_chart.vue'),
            VerticalRadar : () => import('@/components/emap/emap_custom_plugin/pro/vertical_radar_profile.vue'),
            AccumulatedPrecp: () => import('@/components/accumulated_precp/accumulated_precp.vue'),
            FavoritesHint: () => import('@/components/favorites/favorites_hint.vue'),
        },

        props: {
            // 資訊
            information: {
                type: Array,
                default() {
                    return []
                }
            },
            // 地圖
            map_config_from_server: {
                type: Object,
                default() {
                    return {}
                }
            },

            origin_selected_products: {
                type: Array,
                default() {
                    return []
                }
            },

            keep_group_list: {
                type: Array,
                default() {
                    return []
                }
            },

            multiple_group_list: {
                type: Array,
                default() {
                    return []
                }
            },

            timeless_list: {
                type: Array,
                default() {
                    return []
                }
            },

            config: {
                type: Object,
                default() {
                    return {}
                }
            },
            
            // 底圖
            selected_geomap_products: {
                type: Array,
                default() {
                    return []
                }
            },

            // 我的最愛
            is_show_favorites: {
                type: Boolean,
                default: true
            },
        },

        data() {
            return {
                init_opened_layers: [],
                init_items: [],
                init_radios: {},
                tag_position_reference: {},
                selected_items: [],
                filled_timestamp_list :[], //有透過fill_data填補的時間

                kmap: {},

                panes_config: [],
                pane_type_mapping_config: {
                    'pixel': 'normal',
                    'png': 'normal',
                    'alert': 'normal',
                    'alert_inmap': 'normal',
                    'lightning_alert': 'top',
                    'monitor': 'top',
                    'convection': 'top',
                    'max_rain': 'top',
                    'radar_county_warning': 'top',
                    'ty_track': 'top',
                    'lightning': 'top',
                    'station': 'top',
                    'auto': 'top',
                    'gauge': 'top',
                    'plan': 'top',
                    'dropsonde': 'top',
                    'flight': 'top'
                },

                timebar_option: {
                    list: [],
                    props_play_index: -1,
                    duration: 1000,
                },

                RENDER_TYPE: ['pixel', 'symbol', 'multi'],
                NINEBLOCK_TYPE: ['gauge'],

                SUB_TYPE_THAT_SHOULD_USE_PINK_NO_DATA_TAG: ['alert', 'alert_inmap'],

                //geojson:
                description_info: {},

                // geojson alert
                map_tables: {},
                alerts: {},

                // geojson monitor
                is_loaded_monitor_message: false,

                // geojson lightning
                is_loaded_lightning_message: false,

                // geojson typhoon
                is_loaded_typhoon_message: false,

                // geojson ty_track
                typhoon_loop_duration: 1000,
                typhoon_spd_select: [
                    {'text':'X1', 'value':1000},
                    {'text':'X2', 'value':500},
                    {'text':'X4', 'value':250},
                ],
                ty_timer: {},
                typhoon_forecast: false,
                typhoon_forecast_step: 0,

                fit_bounds_record: new Set(),

                // gis
                gis_mutation_observer: null,
                gis_popup_data: {},
                gis_icon_style: {},

                // gis station|auto
                is_loaded_station_message: false,

                // gis gauge
                is_loaded_gauge_message: false,

                // gis dropsonde
                skew_options: {},

                // gis chart
                is_show_chart_dialog: false,
                chart_options: {},

                // geojson airplane_wind
                airplane_wind_chart_options: {},

                chart_type: '', //dropsonde|airplane_wind|wmarker

                geomap_live_data: {},
                traffic_live_display_time: '',
                maptool_colorbars_list: [],
                maptool_legends_list: [],

                gis_popup_type: '',
                popup_type: '',

                // monitor alert table
                all_table_sign_latlng: {

                },
                table_sign: {
                    icon_class: '',
                    signal_class: '',
                    style: {},
                },

                // 目前無顯示或無發佈
                empty_alert_list: [],
                is_show_empty_text: false,
                empty_timer: null,
                nineblock_popup_timer: null,

                // 是否顯示我的最愛提示文字
                is_show_favorites_hint: false,

                // for 我的最愛功能
                webmaps_favorites: {}, // 用來放我的最愛 API 回傳值中的 webmaps_favorites
                favorites_hint_text:'',
            }
        },
        
        computed: {
            ...mapState([
                'client_name',
                'language',
                'display_block_width',
            ]),

            ...mapState('emap', [
                'current_timestamp',
                'selected_timeless_item_id',

                'is_show_alert_table',
                'is_show_typhoon_time',
                'is_show_typhoon_animation',
                'is_show_accumulated_precp_table',
                'is_show_monitor_tooltip',
                'dropsonde'
            ]),

            ...mapState('emap-monitor', [
                'monitor_alert',
                'focal_sign_number',
                'monitor_map',
                'is_monitor_open_emap'
            ]),

            ...mapGetters([
                'is_mobile_width',
                'is_insufficient_height'
            ]),

            ...mapGetters('emap', [
                'radar_id',
                'radar_angle',
                'radar_angle_options',
                'map_status',
            ]),

            typhoon_forecast_tooltip_initial_position () {
                const map_tool_dom = document.querySelector('#map_btn_tool_container')
                const map_tool_position = map_tool_dom.getBoundingClientRect()
                return {
                    x: map_tool_position.x - 200,
                    y: map_tool_position.y
                }
            },

            css_props() {
                return {
                    bottom: this.is_show_chart_dialog ? '0' : '-100%', 
                    width: `${this.display_block_width}px`
                }
            },

            is_show_popup_table() {
                return Object.keys(this.gis_popup_data).length > 0 || Object.keys(this.description_info).length > 0
            },            

            map_config() {
                const get_basic_map_config = () => {
                    if (this.language === 'en') {
                        return MapConfigEN
                    }
                    
                    if(UNITS_PRO.includes(this.client_name)) {
                        return MapConfigPro
                    }

                    return MapConfig
                }

                const basic_map_config = get_basic_map_config()
                Object.entries(this.map_config_from_server).forEach(([first_key, first_values]) => {
                    Object.entries(first_values).forEach(([second_key, second_value]) => {
                        const config_index = ['boundary', 'basemap'].indexOf(first_key)
                        if (config_index >= 0) {
                            if (second_key === 'setting') {
                                Object.entries(second_value).forEach(([third_key, third_value]) => {
                                    basic_map_config['basemap_boundary'][config_index]['setting'][third_key]['url'] = third_value
                                })   
                            } else if (second_key === 'list') {
                                basic_map_config['basemap_boundary'][config_index]['item'][second_key] = second_value
                            } else {
                                basic_map_config['basemap_boundary'][config_index][second_key] = second_value
                            }
                        } else if (first_key === 'options') {
                            basic_map_config[first_key][second_key] = second_value
                        }
                    })
                })

                if(this.client_name === 'FREEWAY') {
                    basic_map_config.options.maxZoom = 14
                }

                return basic_map_config
            },

            wmarker_config() {
                if (Object.keys(this.config).length === 0) return {}

                Object.entries(this.config['gis']).forEach(([gis_key, popup_config]) => {
                    if(WMarkerConfig[gis_key]) {
                        const popup_key = gis_key === 'gauge' ? 'popup_map' : 'popup'
                        WMarkerConfig[gis_key] =  {
                            ...WMarkerConfig[gis_key],
                            [popup_key]: popup_config
                        }
                    }
                })
                return WMarkerConfig
            },

            selected_product_id_set() {
                return this.origin_selected_products.map(({data_name}) => data_name)
            },

            multiple_products_activating() {
                return this.multiple_group_list.reduce((multiple_products_activating, multiple_group_item) => {
                    const multiple_group_item_list = multiple_group_item['list']
                    const is_selected_products_includes_all_multiple_group = difference(multiple_group_item_list, this.selected_product_id_set).length === 0
                    if (is_selected_products_includes_all_multiple_group){
                        return [...multiple_products_activating, ...multiple_group_item_list]
                    }
                    return multiple_products_activating
                }, [])
            },

            selected_products() {
                const selected = this.is_monitor_open_emap ? this.monitor_map : this.origin_selected_products;
                return selected.map(product => {
                    //高鐵站的sub_type要覆寫掉(紹軒那邊需要用thsr區分, 網頁這邊不用)
                    let sub_type = product['data_sub_type']
                    if(sub_type === 'thsr_station') sub_type = 'station'
                    else if(sub_type==='thsr_gauge') sub_type = 'gauge'

                    const product_info_need = {
                        // 更改 property name, 讓使用上更符合認知
                        id: product['data_name'],
                        header_path: product['header_path'],
                        files: product['data_file'] || product['file_path'],
                        timestamp_list: product['timestamp_list'] || [],
                        product_type: product['data_type'],
                        product_sub_type: sub_type,
                        product_bounds: product['data_bounds'],
                        pane_text: product['data_tag'] || product['data_category'],
                        pane_order: product['data_order'],

                        default_sub_data: product['default_sub_data'],
                        sub_data_list: product['sub_data_list'],
                        data_proj: product['data_proj'],
                        display_transparent: product['display_transparent'],
                        data_start_latitude: product['data_start_latitude'],
                        data_start_longitude: product['data_start_longitude'],
                        data_end_latitude: product['data_end_latitude'],
                        data_end_longitude: product['data_end_longitude'],
                        data_unit: product['data_unit'],
                        initial_time_list: product['initial_time_list'],
                        product_message: product['product_message'],
                        colorbar_img: product['colorbar_img'],
                        external: product['external'],
                        information: product['information'],
                        cursor_list: product['cursor_list'],
                        data_vertical: product['data_vertical'],
                        floating_number: product['floating_number']
                    }

                    if (this.multiple_products_activating.includes(product_info_need['id'])) {
                        product_info_need['files'] = product['multiple_data_file']
                        product_info_need['timestamp_list'] = product['multiple_timestamp_list']
                    }
                    return product_info_need
                })
            },

            legend_type_list() {
                return {
                    lightning: this.selected_products
                        .filter( ({product_message}) => product_message === 'lightning_message')
                        .map(({id}) => id),
                    typhoon: this.selected_products
                        .filter( ({product_message}) => product_message === 'typhoon_message')
                        .map(({id}) => id),
                    monitor: this.selected_products
                        .filter( ({product_message}) => product_message === 'monitor_message')
                        .map(({id}) => id),
                    station: this.selected_products
                        .filter( ({product_sub_type}) => ['station', 'auto'].includes(product_sub_type))
                        .map(({id}) => id),
                    gauge: this.selected_products
                        .filter( ({product_sub_type}) => product_sub_type === 'gauge')
                        .map(({id}) => id),
                    traffic_live: this.selected_geomap_products
                        .filter(({product_sub_type}) => product_sub_type === 'freeway_live_traffic' )
                        .map( ({id}) => id )
                }
            },

            product_add_trigger_element() {
                return this.selected_products.map(({ id, files, pane_text, product_type, product_sub_type }) => {
                    return { id, files, pane_text, product_type, product_sub_type }
                })
            },

            check_in_maptool_legends_list() {
                return (type = undefined) => {
                    if (type) {
                        const current_type_maptool_ids = this.maptool_legends_list.filter( id => this.legend_type_list[type].includes(id))
                        
                        return this.legend_type_list[type].length === current_type_maptool_ids.length
                    }
                    return false
                };
            },

            show_map_setting_options() {
                const is_display_simple_layout = (this.is_mobile_width || this.is_insufficient_height)
                const input_limit = is_display_simple_layout ? 1 : 4;

                const legend_products = this.selected_products
                    .filter(({ product_message, product_sub_type }) => {
                        const show_lightning = product_message == 'lightning_message' && this.is_loaded_lightning_message
                        const show_typhoon = product_message == 'typhoon_message' && this.is_loaded_typhoon_message
                        const show_monitor = product_message == 'monitor_message' && this.is_loaded_gauge_message
                        const show_station = ['station', 'auto'].includes(product_sub_type) && this.is_loaded_station_message
                        const show_gauge = product_sub_type == 'gauge' && this.is_loaded_gauge_message
                        return (
                            show_lightning ||
                            show_typhoon ||
                            show_monitor ||
                            show_station||
                            show_gauge
                        )
                    })

                const legend_geomap_products =  this.selected_geomap_products
                    .filter(({ product_sub_type }) => product_sub_type === 'freeway_live_traffic')
                
                let legend_default_checked_id = []
                const legend_items = [...legend_products, ...legend_geomap_products].map((product, index) => {
                    const { id, pane_text, data_category } = product

                    const product_lengend_type = Object.entries(this.legend_type_list).find(([key, item]) => item.includes(id))[0]

                    let default_display = index < input_limit
                    if (default_display) legend_default_checked_id = [...legend_default_checked_id, id]
                    
                    const has_same_legend_ids = this.legend_type_list[product_lengend_type]
                        .some( item => legend_default_checked_id.some( checked_id => item.includes(checked_id)))

                    default_display = has_same_legend_ids || default_display

                    return {
                        id,
                        name: pane_text || data_category,
                        default_display,
                        type: product_lengend_type
                    };
                })
                const is_all_colorbar_items_default_hide = legend_items.length > 0 && (is_display_simple_layout)

                const colorbar_items = this.selected_products
                    .filter(({ product_type, colorbar_img }) => this.RENDER_TYPE.includes(product_type) || colorbar_img)
                    .map(({ id, pane_text }, index) => {
                        return {
                            id,
                            name: pane_text,
                            default_display: is_all_colorbar_items_default_hide ? false : index < input_limit
                        };
                    });

                return {
                    input_type: 'checkbox',
                    legends: { title: this.$t('__legend_color_bar_tool.legend_text'), items: legend_items },
                    colorbars: { title: this.$t('__legend_color_bar_tool.color_bar_text'), items: colorbar_items },
                    input_limit,
                    legend_type_list: this.legend_type_list
                };
            },

            is_show_maptool() {
                const has_tool = this.show_map_setting_options.legends.items.length !== 0 || this.show_map_setting_options.colorbars.items.length !== 0;
                return has_tool;
            },

            is_show_unique_product_feature() {
                const product_message_list = this.selected_products.map(({product_message}) => product_message)
                const has_colorbar_img_item = this.selected_products.find(({colorbar_img}) => colorbar_img)
                const product_sub_type_list =  this.selected_products.map(({product_sub_type}) => product_sub_type)
                return {
                    lightning: product_message_list.includes('lightning_message'),
                    monitor: product_message_list.includes('monitor_message'),
                    max_rain: {
                        src: transform_max_rain_color_bar_image(has_colorbar_img_item?.colorbar_img),
                        id: has_colorbar_img_item?.id
                    },
                    ty_track: product_sub_type_list.includes('ty_track'),
                    station: product_sub_type_list.includes('station') || product_sub_type_list.includes('auto'),
                    gauge: product_sub_type_list.includes('gauge'),
                    vertical_product: this.origin_selected_products.filter(product => product.data_vertical)[0],
                    traffic_live: this.selected_geomap_products.map(({product_sub_type}) => product_sub_type).includes('freeway_live_traffic')
                }
            },

            geomap_render_trigger_element() {
                return this.selected_geomap_products.map(( {id }) => {
                    return {
                        id,
                        live_data: this.geomap_live_data[id] || {}
                    }
                })
            },

            is_show_loading_text() {
                return Object.values(this.panes_config).some(({ is_loading }) => is_loading)
            },

            need_display_alert_table_content() {
                return Object.entries(this.alerts)
                    .filter(([id]) => this.selected_product_id_set.includes(id))
                    .map(([id, values_group_by_timestamp]) => {
                        const selected_product = this.selected_products.find(product => product.id === id)
                        const title = selected_product.pane_text
                        return {
                            title,
                            data: values_group_by_timestamp
                        }  
                    })
                    .reduce((accumulator, values) => {
                        const alert_content_value = values.data[this.current_timestamp]
                        if (alert_content_value) {
                            return {
                                ...accumulator,
                                title: values.title,
                                data: alert_content_value
                            }
                        }
                        return accumulator
                    }, {})
            },

            is_newest_time() {
                return this.current_timestamp === this.timebar_option.list.slice(-1)[0] && this.map_status === 'newest';
            },

            is_show_forecast_tool() {
                return this.typhoon_forecast && this.is_newest_time;
            }
        },

        watch: {
            'timebar_option.list': {
                handler() {
                    if (!this.timebar_option.list.length) this.set_current_timestamp(+ new Date());
                }
            },

            language: {
                immediate: true,
                handler() {
                    Object.values(topline_config).forEach(({name}) => {
                        this.$refs.kxdmap?.delete_pane(name)
                    })
                    this.add_topline()
                }
            },

            map_config() {
                this.kmap.map_config = this.map_config
            },

            wmarker_config() {
                this.kmap.wmarker_config = this.wmarker_config
            },

            product_add_trigger_element: {
                handler(new_elements, old_elements) {
                    // 流程 
                    // 顯示 loading_message => 刪除產品 => 新增產品 => 整理畫面 => 整理時間軸 => 隱藏 loading message
                    const is_value_change = JSON.stringify(new_elements) !== JSON.stringify(old_elements)
                    if (!is_value_change) return 

                    this.delete_products(new_elements, old_elements)
                    this.clear_geojson_popup_data();
                    this.clear_gis_popup_data();

                    if (this.selected_timeless_item_id || new_elements.length === 0 ) {
                        this._clear_timebar_option()
                    } else {
                        this.reconstruct_timebar_parameter()
                    }

                    // 狀況: 資料 雷達10分鐘一筆，閃電1分鐘一筆
                    // 造成問題: 選取特定時間時無法看見兩種資料同時疊在地圖上的樣子
                    // 解決方式: 新增 this.fill_data(); 詳細說明請移至 fill_data() 內查看
                    this.fill_data();
                    this.add_all_render_type_products();
                    this.add_all_other_products();

                    // 舊版提供的顯示所有產品都有資料的時間的功能，新 UIUX 不提供
                    new_elements.length > 0 && this.calculate_latest_complete_data_timestamp()

                    this.show_empty_alert()
                }
            },

            radar_angle(new_value, old_value) {
                // init不觸發，切換時才觸發
                if (old_value && this.radar_id) {
                    const radar_product = this.selected_products.find(({id}) => id === this.radar_id)
                    this.$refs.kxdmap.delete_pane(this.radar_id)
                    this.add_render_pixel(radar_product)
                }
            },

            is_show_typhoon_time() {
                show_or_hide_typhoon_time_display(this.is_show_typhoon_time)
            },

            is_show_typhoon_animation() {
                this.start_or_stop_typhoon_animation(this.is_show_typhoon_animation)
            },

            "gis_popup_data.id"(new_id, old_id) {
                if (old_id) this.gis_mutation_observer.disconnect()
                if (new_id) {   
                    const station_selector = `#${new_id}`
                    const gis_mutation_observer_config = { attributes: true }
                    this.gis_mutation_observer = init_mutation_observer(() => {
                        const station_dom = document.querySelector(station_selector)
                        if (!station_dom) {
                            return this.gis_icon_style = {
                                opacity: 0,
                            };    
                        }

                        this.gis_icon_style = {
                            transform: `translate(-50%, -120%) ${calc_gis_tooltip_style(station_selector)}`,
                            opacity: 1,
                        };
                    })

                    this.gis_mutation_observer.observe(document.querySelector('.leaflet-map-pane'), gis_mutation_observer_config);
                    this.gis_mutation_observer.observe(document.querySelector(station_selector).parentElement, gis_mutation_observer_config)
                } 

                if (this.is_show_popup_table) {
                    this.chart_options = this.gis_popup_data?.feature?.api_data ?? {}
                }
            },

            is_show_popup_table() {
                if (!this.is_show_popup_table) this.close_chart_dialog()
            },

            'dropsonde.hPa'() {
                const { id: dropsonde_id } = this.dropsonde
                const dropsonde_product = this.selected_products.find(({ id }) => id === dropsonde_id)
                const data_file_index = dropsonde_product.files.findIndex(({ data_time_timestamp }) => data_time_timestamp === this.current_timestamp)
                this.$refs.kxdmap.delete_pane(this.dropsonde_id)
                this.add_gis_dropsonde(dropsonde_product, data_file_index)
            },

            selected_geomap_products() {
                this.selected_geomap_products.forEach(({id, product_type, api_path}) => {
                    if(product_type === 'external' && api_path) {
                        if(!this.geomap_live_data[id]) {
                            this.geomap_live_data[id] = {}
                        }
                        
                        if(this.geomap_live_data[id]['interval_id']) {
                            return
                        }

                        this.geomap_live_data[id]['interval_id'] = setInterval(async () => {
                            const with_client_name_api_path = `${this.client_name}/${api_path}`
                            await this._assign_geomap_live_data(id, with_client_name_api_path)
                        }, 60000)
                    }
                })
            },

            geomap_render_trigger_element(new_elements, old_elements) {
                this.delete_products(new_elements, old_elements, 'geomap')
                this.add_geomap_products()
            },

            monitor_alert() {
                const is_show_monitor_table_alert_geojson = this.monitor_alert.file_path
                if (this.$refs.kxdmap.panes_config.table_geojson) this.$refs.kxdmap.delete_pane('table_geojson')
                if (is_show_monitor_table_alert_geojson) {
                    this.close_chart_dialog()
                    this.add_table_geojson(this.monitor_alert)
                    if (this.focal_sign_number) {
                        setTimeout(() => {
                            const { icon_class, signal_class } = this.monitor_alert.alert_content[this.focal_sign_number] ?? {}
                            const focal_monitor_selector = `.sign_${this.focal_sign_number}`
                            if (focal_monitor_selector) {
                                this.table_sign = {
                                    icon_class,
                                    signal_class,
                                    style: {
                                        transform: `translate(-50%, -125%) ${calc_focal_sign_tooltip_style(focal_monitor_selector)}`,
                                        opacity: 1,
                                    }
                                }
                            }

                        }, 500)

                    }
                }
            },

            focal_sign_number(new_focal_number, old_focal_number) {
                if (old_focal_number) this.gis_mutation_observer?.disconnect()

                if (!new_focal_number) {
                    return this.table_sign = {}
                }

                const focal_monitor_selector = `.sign_${this.focal_sign_number}`
                const { icon_class, signal_class } = this.monitor_alert.alert_content[this.focal_sign_number] ?? {}
                const { lat, lng } = this.all_table_sign_latlng[this.focal_sign_number]
                this.kmap.set_map_view({
                    latlng: [lat, lng], 
                    zoom: 11
                })
                this.table_sign = {
                    icon_class,
                    signal_class,
                    style: {
                        transform: `translate(-50%, -125%) ${calc_focal_sign_tooltip_style(focal_monitor_selector)}`,
                        opacity: 1,
                    }
                }

                const gis_mutation_observer_config = { attributes: true }
                this.gis_mutation_observer = init_mutation_observer(() => {
                    this.table_sign.style = {
                        transform: `translate(-50%, -125%) ${calc_focal_sign_tooltip_style(focal_monitor_selector)}`,
                        opacity: 1,
                    }
                })

                this.gis_mutation_observer.observe(document.querySelector('.leaflet-map-pane'), gis_mutation_observer_config);
                this.gis_mutation_observer.observe(document.querySelector(focal_monitor_selector), gis_mutation_observer_config) 
            },

            favorites_hint_text(new_value, old_value) {
                if (new_value != '') {
                    this.is_show_favorites_hint = true;
                    setTimeout((() => {
                        this.is_show_favorites_hint = false;
                        this.favorites_hint_text = '';
                    }),1000);
                }
            },
            chart_type(type){
                if(!type) return

                // 一次只開一個時序圖, 切換種類時清掉舊的
                if(type !== 'dropsonde' && Object.keys(this.skew_options).length){
                    this.skew_options = {}
                }
                else if(type !== 'airplane_wind' && Object.keys(this.airplane_wind_chart_options).length){
                    this.airplane_wind_chart_options = {}
                }
            },
        },

        mounted() {
            this.initial_kmap_variable();
        },

        methods: {
            ...mapMutations('emap', [
                'set_current_timestamp',
                'set_radar',
                'set_dropsonde',
                'set_is_show_typhoon_animation',
                'set_is_show_monitor_tooltip',
            ]),

            ...mapMutations('emap-monitor', [
                'set_focal_sign_number'
            ]),

            set_maptool_list (type, data) {
                if (type == 'colorbars') {
                    this.maptool_colorbars_list = data;
                } else {
                    this.maptool_legends_list = data;
                }
            },

            initial_kmap_variable() {
                this.kmap = {
                    id: '1',
                    map_bg_color: '#000',
                    map_config: this.map_config,
                    wmarker_config: {},
                    panes: {},// for kxd-map, 可控制pane
                }
            },

            show_empty_alert() {
                this.empty_alert_list = this.empty_alert_list.filter( ({pane_text}) => pane_text !== '自選時段累積雨量')
                if (!this.empty_alert_list.length) return
                if (this.is_show_empty_text) {
                    // 目前推斷，自動更新，可能在未知的情況下，會導致重複執行的狀況
                    clearTimeout(this.empty_timer)
                    this.empty_timer = null
                    this.is_show_empty_text = false
                }
                this.is_show_empty_text = true
                
                this.empty_timer = setTimeout(() => {
                    this.is_show_empty_text = false
                    this.empty_alert_list = []
                } , 2500)
            },

            add_topline() {
                const _create_top_line  = async ({
                    name, url, order, weight,
                    color, hide_line_zoom, map_text_tag
                }, csv_data_callback) => {
                    
                    const res = await get_remote_file_ajax(map_text_tag['url'][this.language])
                    const csv_data = res.data
                    const map_tag_list = csv_data
                        .split('\n')
                        .map(csv_data_callback)
                        .filter(datum => datum)

                    // 不能跟 render_map 的名字重複
                    const name_text = `${name}` 

                    this.kmap.create_layer({
                        name: name_text,
                        type: 'geojson',
                        pane_order: order,
                        url: url,
                        render_hint: { 
                            draw_canvas: true,
                            save_layer_data: false
                        },
                        order_tool: false,
                        opacity_tool: false,
                        pane_tag_tool: false,
                        style: { 
                            color: color['dark_map'],
                            weight: weight,
                            fillOpacity: 0,
                        },
                        onLayerDidMount() {
                            return { data: map_tag_list }
                        },
                        onDrawLayer(info, data) {
                            _hide_line(name_text, info, hide_line_zoom)

                            const ctx = info.canvas.getContext('2d')
                            ctx.clearRect(0, 0, info.canvas.width, info.canvas.height)
    
                            if (info.zoom < map_text_tag['hide_zoom']) return

                            data.forEach(datum => {
                                const text = datum.text
                                const latlng = datum.latlng
    
                                if (info.bounds.contains(latlng)) {
                                    const dot = info.layer._map.latLngToContainerPoint(latlng)
                                    ctx.fillStyle = map_text_tag['color']['dark_map']
                                    ctx.font = '12px 新細明體'
                                    ctx.textAlign = 'center'
                                    ctx.textBaseline = 'middle'
                                    ctx.fillText(text, dot.x, dot.y)
                                }
                            })
                        },
                    })
                }
                
                const _hide_line = (name, info, hide_line_zoom) => {
                    if (hide_line_zoom) {
                        if ( info.zoom < hide_line_zoom ) {
                            // 不能移除 pane，事件會消失
                            // vm.kmap.remove_pane('town')
                            document.querySelector(`.leaflet-${name}-pane`).style.opacity = 0
                        }else {
                            document.querySelector(`.leaflet-${name}-pane`).style.opacity = 1
                        }
                    }
                } 

                const county_data_csv_callback = (row_datum) => {
                    if (row_datum) {
                        const datum = row_datum.split(',');
                        return {
                            text: datum[0],
                            latlng: [Number(datum[2]), Number(datum[1])]
                        };
                    }
                }

                const town_data_csv_callback = (row_datum) => {
                    if (row_datum) {
                        const datum = row_datum.split(',');
                        return {
                            text: datum[1],
                            latlng: [Number(datum[3]), Number(datum[2])]
                        };
                    }
                }

                _create_top_line(topline_config['county'], county_data_csv_callback)
                _create_top_line(topline_config['town'], town_data_csv_callback)
            },

            assign_panes_config(list) {
                this.panes_config = list
            },

            delete_products(new_elements, old_elements, type='emap') {
                this.typhoon_forecast = false;
                
                if (type !== 'geomap') {
                    // 初始化 legend，讓地圖上沒有資料的時候，不顯示，legend_color_bar_tool的控制項也消失
                    this.is_loaded_monitor_message = false
                    this.is_loaded_lightning_message = false
                    this.is_loaded_typhoon_message = false
                    this.is_loaded_station_message = false
                    const has_nineblock_product = new_elements
                        .filter(({product_sub_type}) => this.NINEBLOCK_TYPE.includes(product_sub_type)).length > 0
                    if (!has_nineblock_product) this.is_loaded_gauge_message = false
                }

                const clear_product_id_set = old_elements
                    .filter(old_element => {
                        // 任何形式有不同的產品包含
                        // 被取消選取的、pane_text 欄位: 中英文切換、file欄位: 自動更新, 切換時間
                        const old_id = old_element.id
                        const new_element = new_elements.find(({id}) => id === old_id)
                        const is_not_new = new_element && JSON.stringify(new_element) === JSON.stringify(old_element)
                        if (is_not_new) return false
                        return true
                    })
                    .map(({id}) => id)

                clear_product_id_set.forEach(id => {
                    this.$refs.kxdmap.clear_pane(id)
                })

                // 被取消選取的產品
                const cancel_selected_product = old_elements
                    .filter(old_element => {
                        const old_id = old_element.id
                        return !new_elements.find(({id}) => id === old_id)
                    })
                    .map(({id}) => id)
                cancel_selected_product.forEach(id => {
                    // clear radar_echo product
                    if(this.radar_id === id) {
                        this.set_radar({
                            id: '',
                            angle: '',
                            angle_options: [],
                        })
                    }

                    if (this.fit_bounds_record.has(id)) {
                        this.fit_bounds_record.delete(id)
                    }
                    
                    if(this.geomap_live_data[id]?.interval_id) {
                        clearInterval(this.geomap_live_data[id]?.interval_id)
                        this.geomap_live_data[id] = {}
                    }

                    this.$refs.kxdmap.delete_pane(id)
                })
            },

            _clear_timebar_option() {
                this.timebar_option.list = []
                this.timebar_option.props_play_index = -1
            },
            
            reconstruct_timebar_parameter() {
                const all_timestamp_list = this.selected_products.reduce((accumulator, { timestamp_list }) => {
                    return [...accumulator, ...timestamp_list]
                }, []);
                this.timebar_option.list = [...new Set(all_timestamp_list)].sort((a, b) => a - b)
                const last_index = this.timebar_option.list.length - 1
                this.timebar_option.props_play_index = last_index
                this.set_current_timestamp(this.timebar_option.list[last_index])
            },

            calculate_latest_complete_data_timestamp() {
                const all_timestamp_list = this.selected_products.map(({ timestamp_list }) => timestamp_list)
                const intersection_timestamp_list = intersection(...all_timestamp_list)
                const parsed_filled_timestamp_list = intersection_timestamp_list.filter(t => !this.filled_timestamp_list.includes(t))

                this.$set(this.timebar_option, 'latest_complete_data_timestamp', parsed_filled_timestamp_list[parsed_filled_timestamp_list.length - 1])
            },

            change_time(index, timestamp) {
                this.selected_products.forEach(product => {
                    const {id, product_type, product_sub_type} = product
                    if (this.RENDER_TYPE.includes(product_type)) {
                        const is_product_loading = this.$refs.kxdmap.panes_config[id]?.is_loading
                        if (!is_product_loading) { // 資料還沒讀完就換頁，程式會報錯
                            const is_trigger_keep_group_feature = this._check_keep_group_feature_trigger(id)
                            let be_used_timestamp = timestamp
                            if (is_trigger_keep_group_feature) {
                                be_used_timestamp = this._get_closet_keep_group_timestamp(id, timestamp)
                            }
                            this._control_render_type_display(id, be_used_timestamp)
                        }
                        return
                    }

                    // 為了修正地圖自動雨量站換時間會畫不出圖的問題，所以把這句註解(暫不確定是否會造成其他問題)
                    // if (this.NINEBLOCK_TYPE.includes(product_sub_type)) return this._nineblock_render(product, this.current_timestamp)

                    // other type
                    this.$refs.kxdmap.remove_pane(id)
                    this.$refs.kxdmap.clear_pane(id)
                    
                    this.add_other_product(product)
                })
            },

            _check_keep_group_feature_trigger(id) {
                // keep_group_list 
                // main_product 表示選到的產品
                // keep_list 表示附屬的產品
                // 當選到的產品時間點，附屬的產品沒有資料時，嘗試尋找往前推 20 分鐘的最近資料顯示在地圖上
                return this.keep_group_list.some(({main_product, keep_list}) =>  {
                    return this.selected_product_id_set.includes(main_product) && keep_list.includes(id)
                })
            },

            _get_closet_keep_group_timestamp(be_kept_product_id, timestamp) {
                const closet_timestamp = this.selected_products
                    .find(({id}) => id === be_kept_product_id).timestamp_list
                    .filter(keep_item_timestamp => keep_item_timestamp <= timestamp)
                    .sort((prev, next) => next - prev)[0]
                return timestamp - closet_timestamp <= 1200000 ? closet_timestamp : -1
            },

            _control_render_type_display(id, timestamp) {
                if (!this.kmap.panes[id]?.render?.is_exist(`${id}_${timestamp}`)) {
                    // 選取產品時會非同步讀取產品，所以若產品不存在，跑時間軸時，仍然要重新確認產品是否可供加入時間軸
                    const product = this.selected_products.find(product => product.id === id)
                    const is_product_has_multiple_radar_angle = product.default_sub_data
                    const files = is_product_has_multiple_radar_angle ? product.files[this.radar_angle] : product.files
                    const current_file = files.find(({ data_time_timestamp }) => data_time_timestamp === timestamp)
                    if (current_file) {
                        if (!this.kmap.panes[id].options.is_on_map || !this.kmap.panes[id]?.render) return this.add_render_type_product(product)
                        const format_url_function = FORMAT_URL_CALLBACK_MAPPING[product.product_type]

                        this.kmap.panes[id].render.add_data({
                            data: {
                                key: `${id}_${timestamp}`,
                                url: format_url_function(current_file)
                            },
                            callback: () =>  {
                                this.kmap.panes[id]?.render?.change(`${id}_${timestamp}`)   
                                this.$refs.kxdmap.panes_config[id].is_on_map = true
                                this.kmap.add_pane(id)        
                            }
                        })
                    } else {
                        this.$refs.kxdmap.panes_config[id].is_on_map = false
                        this.$refs.kxdmap.clear_pane(id)
                    }
                } else {
                    this.kmap.panes[id]?.render?.change(`${id}_${timestamp}`)
                    this.$refs.kxdmap.panes_config[id].is_on_map = true
                    this.kmap.add_pane(id)
                }
            },

            add_all_render_type_products() {
                if (this.selected_timeless_item_id) {
                    const single_product = this.selected_products[0]
                    // 自選時段累積雨量
                    if (single_product['files'].length === 0) {
                        return this.add_empty(single_product, single_product.data_type)
                    }
                }

                this.selected_products
                    .filter(({ product_type }) => this.RENDER_TYPE.includes(product_type))
                    .forEach((product) => {
                        this.add_render_type_product(product)
                    })
            },

            add_render_type_product(product) {
                const { default_sub_data, product_type } = product

                const commit_vertical_radar_parameter = ({ sub_data_list, id, default_sub_data }) => {
                    const is_need_init_radar_angle = !this.radar_angle || !sub_data_list.includes(this.radar_angle)
                    is_need_init_radar_angle && this.set_radar({ angle: default_sub_data })
                    
                    this.set_radar({
                        id,
                        angle_options: sub_data_list,
                    })
                }

                let { files } = product
                if (typeof files !== 'string') {
                    if (default_sub_data) {
                        commit_vertical_radar_parameter(product)
                        files = get_product_with_current_radar_angle(product, this.radar_angle).files
                    }

                    if (!this.selected_timeless_item_id) {
                        // 除了自選時段雨量這種特殊產品外的產品
                        const is_current_time_file_exist = files.some(({data_time_timestamp}) => data_time_timestamp === this.current_timestamp)
                    
                        if (files.length === 0 || !is_current_time_file_exist) return this.add_empty(product, product_type)
                    }
                }

                if (this.RENDER_TYPE.includes(product_type)) {
                    const render_product_mapping = {
                        pixel: this.add_render_pixel,
                        symbol: this.add_render_symbol,
                        multi: this.add_render_multi,
                    }

                    const add_func = render_product_mapping[product_type]
                    add_func(product)
                }
            },

            // 原本的 grid 裡面需包含 pixel symbol multi，現在 map 的 type 命名為 render
            add_render_pixel(product) {
                const { id, product_type, default_sub_data, data_unit, data_vertical, floating_number } = product 
                // 與仰角回波有關
                const { header_path, files } = default_sub_data && this.radar_angle ? get_product_with_current_radar_angle(product, this.radar_angle) : product
                // 與客製化colorbar有關
                const { val_callback } = get_render_colorbar_val_callback(product)
                
                let pixel_data_list 
                if (this.selected_timeless_item_id) {
                    pixel_data_list = generate_accumulated_precp_data({ header_path, files, id })
                } else {
                    pixel_data_list = generate_data_by_render_sub_type(
                        { header_path, files, id, product_type }, 
                        this.current_timestamp, 
                    )
                }

                this.kmap.create_layer({
                    ...get_common_create_render_type_options(product, this.selected_products), 
                    data: pixel_data_list,
                    render_hint: {
                        sub_type: 'pixel',
                        colorbar: {
                            unit: data_unit,
                            floating_number: floating_number || 0,
                            cursor_list: [{ title: 'Q', unit: '' }],
                            val_callback: val_callback
                        }
                    },
                    pane_tag_config: {
                        // for forecast product
                        initial_time_list: product['initial_time_list'] ? product['initial_time_list'] : [],
                        vertical: data_vertical
                    },
                })
            },

            add_render_symbol(product) {
                const { data_unit, cursor_list, product_sub_type, data_vertical, floating_number } = product
                const symbol_data_list = generate_data_by_render_sub_type(
                    product,
                    this.current_timestamp,  
                )
                const default_cursor_list = [
                    { title: 'H', unit: '' },
                    { title: 'Q', unit: '' },
                ]
                // 與客製化colorbar有關
                const { val_callback } = get_render_colorbar_val_callback(product)

                const sub_type = product_sub_type === 'symbol_wind' ? product_sub_type : 'symbol'
                const render_hint = {
                    sub_type: sub_type,
                    colorbar: {
                        unit: data_unit,
                        floating_number: floating_number || 0,
                        cursor_list: cursor_list ?? default_cursor_list,
                        val_callback: val_callback
                    }
                }
                if (sub_type === 'symbol_wind') render_hint.multi_drawer = get_symbol_wind_style_drawer()

                if (['id_370', 'id_371'].includes(product.id)) {
                    // WISSDOM風場因為有3D剖面功能，所以 this.kmap.create_layer 參考 add_render_multi 裡的寫法
                    
                    // RWRF短期預報要使用uv檔
                    if (product.id === 'id_371') {
                        render_hint.kxd_version = 2
                        render_hint.sub_type = 'symbol_wind'
                        render_hint.layer = {
                            dataTrans: 'uv', //uv分量
                            windbarColor: null,
                        }
                        render_hint.colorbar.cursor_list = [
                            { title: 'ws', unit: '' },
                            { title: 'wd', unit: '' },
                        ]
                    }  
                    // WISSDOM風場
                    else if(product.id === 'id_370'){
                        render_hint.kxd_version = 2
                        render_hint.sub_type = 'symbol_wind'
                        render_hint.colorbar.cursor_list = [
                            { title: 'ws', unit: '' },
                            { title: 'wd', unit: '' },
                        ]
                        render_hint.layer = {
                            windbarColor: null,
                        }
                    }
                    
                    this.kmap.create_layer({
                        ...get_common_create_render_type_options(product, this.selected_products), 
                        data: symbol_data_list,
                        render_hint, 
                        pane_tag_config: {
                            // for forecast product
                            initial_time_list: product['initial_time_list'] ? product['initial_time_list'] : [],
                            vertical: data_vertical
                        }
                    })
                } else {
                    // 一般 data_type: symbol 產品都是進這裡跑
                    this.kmap.create_layer({
                        ...get_common_create_render_type_options(product, this.selected_products), 
                        data: symbol_data_list,
                        render_hint,
                    })
                }
            },

            add_render_multi(product) {
                const { data_unit, cursor_list, data_vertical } = product
                const multi_data_list = generate_data_by_render_sub_type(
                    product, 
                    this.current_timestamp,  
                )
                const default_cursor_list = [
                    {"title": "H", "unit": ""},
                    {"title": "Q", "unit": ""},
                    {"title": "D", "unit": ""}
                ]

                this.kmap.create_layer({
                    ...get_common_create_render_type_options(product, this.selected_products), 
                    data: multi_data_list,
                    render_hint: {
                        sub_type: 'multi',
                        multi_drawer: get_wind_bar_style_multi_drawer(),
                        colorbar: {
                            unit: data_unit,
                            cursor_list: cursor_list ?? default_cursor_list,
                            cursor_width: 3
                        }
                    }, 
                    pane_tag_config: {
                        // for forecast product
                        initial_time_list: product['initial_time_list'] ? product['initial_time_list'] : [],
                        vertical: data_vertical
                    },                   
                })
            },

            add_all_other_products() {
                // 需要一個一個時間軸讀資料的產品
                if (this.selected_timeless_item_id) {
                    const single_product = this.selected_products[0]

                    // 14日集水區 (現在已是未來1-9日集水區逐日的3日累積雨量)
                    if (single_product['product_sub_type'] === 'geomap') {
                        return this.add_geojson_geomap(single_product)
                    }
                }

                this.selected_products
                    .filter(({ product_type }) => !this.RENDER_TYPE.includes(product_type))
                    .forEach((product) => {
                        this.add_other_product(product)
                    })
            },

            add_other_product(product) {
                const { product_type, product_sub_type, files } = product
                const input_product_type = product_type === 'png' ? product_type : product_sub_type
                const data_file_index = files.findIndex(({ data_time_timestamp }) => data_time_timestamp === this.current_timestamp)
                if (data_file_index === -1) return this.add_empty(product, input_product_type)

                const ohter_product_mapping = {
                    png: this.add_image_png,
                    alert: this.add_geojson_alert,
                    alert_inmap: this.add_geojson_alert_inmap,
                    lightning_alert: this.add_geojson_lightning_alert,
                    monitor: this.add_geojson_monitor,
                    marine_weather: this.add_geojson_marine_weather,
                    convection: this.add_geojson_convection,
                    max_rain: this.add_geojson_max_rain,
                    radar_county_warning: this.add_geojson_radar_county_warning,
                    ty_track: this.add_geojson_ty_track,
                    lightning: this.add_geojson_lightning,
                    plan: this.add_geojson_plan,
                    dropsonde: this.add_gis_dropsonde,
                    station: this.add_gis_station,
                    auto: this.add_gis_auto,
                    gauge: this.add_nineblock_gauge,
                    flight: this.add_gis_flight,
                    airplane_wind: this.add_geojson_airplane_wind,
                }

                this.typhoon_forecast = false;

                const add_func = ohter_product_mapping[input_product_type]
                add_func(product, data_file_index)
            },

            add_empty(product, product_type) {
                const is_in_empty_alert_list = this.empty_alert_list.some( data => data.pane_text === product.pane_text)
                if (!is_in_empty_alert_list) this.empty_alert_list = [...this.empty_alert_list, product]
                
                const { product_sub_type } = product
                this.kmap.create_layer({
                    ...get_common_create_layer_options(product), 
                    is_on_map: false,
                    pane_type: this.pane_type_mapping_config[product_type],
                    pane_tag_config: {
                        no_data: this.SUB_TYPE_THAT_SHOULD_USE_PINK_NO_DATA_TAG.includes(product_sub_type) ? 'pink' : '',
                        ...product_sub_type === 'ty_track' && { data_empty: get_typhoon_empty_data_config(this.language) }
                    },
                })
            },
            
            // 原本的 png
            add_image_png(product, data_file_index) {
                const {
                    data_proj,
                    data_start_latitude, data_start_longitude, 
                    data_end_latitude, data_end_longitude, 
                } = product
                const file_path = get_file_path(product, data_file_index)
                this.kmap.create_layer({
                    ...get_common_create_layer_options(product), 
                    url: file_path,
                    type: 'image',
                    pane_opacity: calculate_pane_opacity(this.selected_products),
                    render_hint: {
                        bounds: [
                            [data_start_latitude, data_start_longitude],
                            [data_end_latitude, data_end_longitude]
                        ],
                        proj: data_proj
                    },
                })
            },

            // 原本的 geojson
            add_geojson_alert(product, data_file_index) {
                const { id } = product
                this.kmap.create_layer({
                    pane_type: 'top',
                    ...this._get_common_create_geojson_type_options(product, data_file_index), 
                    ...this._get_common_create_geojson_alert_options(id),
                    ...this._get_common_geojson_set_style_options(product),
                    onEachFeature: (feature, layer) => {
                        set_layer_popup_descriptions(layer, feature, this.language, '');
                        this._add_alert_table_content(feature, id)
                    },
                })
            },

            add_geojson_alert_inmap(product, data_file_index) {
                const { id } = product
                this.kmap.create_layer({
                    ...this._get_common_create_geojson_type_options(product, data_file_index), 
                    ...this._get_common_create_geojson_alert_options(id),
                    ...this._get_common_geojson_set_style_options(product),
                    pane_type: 'top',
                    onEachFeature: (feature, layer) => {
                        if (id === 'id_176') { // 海象預報
                            const content = this.language==='tw' ? feature.properties.ChName : feature.properties.EnName
                            layer.bindTooltip(content, {className: 'whitespace_nowrap', sticky: true});
                        }

                        const popup_data = create_bind_marker_data_by_config(product, feature.properties, this.config)
                        set_click_event.call(
                            this,
                            layer, 
                            {
                                product,
                                feature,
                                language: this.language,
                                popup_data
                            }
                        );
                    },
                })
            },

            add_geojson_lightning_alert(product, data_file_index) {
                const { id } = product
                this.kmap.create_layer({
                    ...this._get_common_create_geojson_type_options(product, data_file_index), 
                    ...this._get_common_geojson_bind_popup_description_options(product),
                    ...this._get_common_geojson_set_style_options(product),
                    pane_type: 'top',
                    pointToLayer: (feature, latlng) => {
                        this._add_alert_table_content(feature, id)
                        const my_icon = get_icon_marker_options(feature)
                        return L.marker(latlng, {icon: my_icon, pane: id});
                    },
                    destroy: () => {
                        this.alerts[id] = {}
                    }
                })
            },

            add_geojson_monitor(product, data_file_index) {
                const { id } = product
                const is_radar_echo_alert = id === 'id_899' //是否為回波警示表

                if (is_radar_echo_alert) {
                    //回波警示表tooltip預設為: 顯示
                    this.set_is_show_monitor_tooltip(true)
                }

                this.kmap.create_layer({
                    ...this._get_common_create_geojson_type_options(product, data_file_index), 
                    ...this._get_common_geojson_bind_popup_description_options(product),
                    ...this._get_common_geojson_set_style_options(product),
                    pane_type: 'top',
                    pointToLayer: (feature, latlng) => {
                        this._add_feature_reference(feature, product) 
                        const my_icon = get_icon_marker_options(feature)

                        let marker = L.marker(latlng, {
                            icon: my_icon, 
                            pane: id
                        });

                        // 地圖產品為「回波警示表」時，marker 建立 tooltip
                        if (is_radar_echo_alert) {
                            let feature_data = feature;
                            let standby_area_name = Object.keys(feature_data.properties)[0];
                            let tooltip_text = `<div class="tooltip_id_899"> <div class="standby_area_id_899">${standby_area_name}</div>`;
                            // 建立每個點的 tooltip
                            for (let x in feature_data.properties) {
                                let light_list = Object.values(feature_data.properties[x]);
                                light_list.forEach(l => {
                                    let bg_color = (l == '0.0') ? '#00ff00' : (l == '1.0') ? '#ff0000' : 'transparent';
                                    tooltip_text += `<div 
                                        class="light_id_899"
                                        style="background-color: ${bg_color};"
                                    > 
                                    </div>`;
                                })
                                tooltip_text += '</div>';
                            }

                            // 點擊 marker 控制 tooltip 開關
                            let is_open = true;
                            marker.bindTooltip(tooltip_text, { 
                                permanent: true, 
                                direction: 'right',
                                className: 'marker_tooltip'
                            })
                          
                            marker.on('click', ()=> {
                                if (is_open) 
                                    marker.closeTooltip();
                                else 
                                    marker.openTooltip();
                                
                                is_open = !is_open;
                            });
                        }

                        return marker;
                    },
                    callback: (options, pane) => {
                        this.is_loaded_monitor_message = true
                        
                        if (is_radar_echo_alert) {
                            options.pane_tag_config.toggle_tooltip = () => {
                                pane.getLayers().forEach(layer => {
                                    layer.getLayers().forEach(layer => {
                                        if (this.is_show_monitor_tooltip) 
                                            layer.openTooltip();
                                        else 
                                            layer.closeTooltip();
                                    })
                                })
                            }
                        }
                    },
                    render_hint: {
                        save_layer_data: false
                    },
                    pane_tag_config: {
                        toggle_tooltip_btn: is_radar_echo_alert, //回波警示表的開關按鈕
                    },
                })
            },

            add_geojson_marine_weather(product, data_file_index) {
                const { id } = product
            
                this.kmap.create_layer({
                    ...this._get_common_create_geojson_type_options(product, data_file_index), 
                    ...this._get_common_geojson_set_style_options(product),
                    pane_type: 'top',
                    callback: (options, pane) => {
                    },
                })
            },

            add_geojson_convection(product, data_file_index) {
                const { id } = product
                this.kmap.create_layer({
                    ...this._get_common_create_geojson_type_options(product, data_file_index), 
                    ...this._get_common_geojson_set_style_options(product),
                    pane_type: 'top',
                    pointToLayer: (feature, latlng) => {
                        const layer_group = L.featureGroup()

                        if (feature['marker-text']) {
                            const label_text = this._get_marker_icon_text({
                                marker_text: feature['marker-text'], 
                                color: feature['text-color'],
                            })
                            layer_group.addLayer(L.marker(latlng, {
                                icon: label_text,
                                pane: id,
                            }))
                        }

                        const my_icon = get_icon_marker_options(feature)
                        const marker = L.marker(latlng, {
                            icon: my_icon, 
                            pane: id}
                        );
                        const popup_data = create_bind_marker_data_by_config(product, feature.properties, this.config)
                        set_click_event.call(
                            this, 
                            marker, 
                            {
                                product,
                                feature,
                                popup_data,
                            }
                        );
                        layer_group.addLayer(marker)

                        return layer_group
                    },
                    destroy: () => {
                        this.clear_geojson_popup_data()
                    },
                    callback: () => {
                        this.clear_geojson_popup_data()
                    }  
                })
            },

            add_geojson_max_rain(product, data_file_index) {
                const { id } = product
                this.kmap.create_layer({
                    ...this._get_common_create_geojson_type_options(product, data_file_index), 
                    ...this._get_common_geojson_bind_popup_description_options(product),
                    pane_type: 'top',
                    pointToLayer: (feature, latlng) => {
                        this._add_feature_reference(feature, product);
                        const my_icon = get_icon_marker_options(feature);
                        return L.marker(latlng, {icon: my_icon, pane: id});
                    },
                    render_hint: {
                        save_layer_data: false
                    },
                })
            },

            add_geojson_radar_county_warning(product, data_file_index) {
                const { id } = product
                this.kmap.create_layer({
                    ...this._get_common_create_geojson_type_options(product, data_file_index), 
                    pane_type: 'top',
                    pointToLayer: (feature, latlng) => {                            
                        const reference_callback_options = {
                            onEachFeature: (source_feature, layer) => {
                                const properties = feature.properties
                                const get_alert_reason = (reasons) => reasons ? reasons.join('<br>') : '未取得警示資訊'
                                const alert_reason = get_alert_reason(properties.reason)
                                const tooltip_html = `
                                    鄉鎮區：${properties?.name ?? '未取得鄉鎮區資料'} <br>
                                    <div style="display: flex">
                                        <span>警示原因：</span>
                                        <span>${alert_reason}</span>
                                    </div>
                                `
                                layer.bindTooltip(tooltip_html)

                                const popup_data = {
                                    '鄉鎮區': properties?.name ?? '未取得鄉鎮區資料',
                                    '警示原因': alert_reason,
                                    '聯絡電話': properties?.telephone ?? '未取得該鄉鎮聯絡資訊'
                                }
                                set_click_event.call(
                                    this,
                                    layer, 
                                    {
                                        product,
                                        feature,
                                        language: this.language,
                                        popup_data
                                    }
                                );
                            }
                        } 
                        this._add_feature_reference(feature, product, reference_callback_options);
                        const my_icon = get_icon_marker_options(feature);
                        return L.marker(latlng, {icon: my_icon, pane: id});
                    },
                    render_hint: {
                        save_layer_data: false
                    },
                })
            },

            add_geojson_ty_track(product, data_file_index) {
                if (
                    product.id === 'id_46' && 
                    this.$refs.kxdmap.panes_config[product.id]
                ) {
                    this.$refs.kxdmap.remove_pane(product.id) // 不明原因 delete 以後，還是會出現兩層颱風，先 remove 才會只有一層
                    this.$refs.kxdmap.delete_pane(product.id)
                } // 這例外是因為颱風在切換語言後，不會重新繪製，沒辦法重新 assign_description

                this._call_fit_bounds(product)

                const { id } = product
                this.ty_timer[id] = this.ty_timer[id] || {
                    max_step: 0,
                    timer: null
                };

                this.kmap.create_layer({
                    ...this._get_common_create_geojson_type_options(product, data_file_index),
                    ...this._get_common_geojson_bind_popup_description_options(product),
                    pane_type: 'top',
                    pane_tag_config: {
                        data_empty: get_typhoon_empty_data_config(this.language)
                    },
                    style: (feature) => {
                        const step = feature.animation_step
                        if (step) {
                            this.ty_timer = update_ty_animation_timer_structure(this.ty_timer, id, step)
                            feature = {
                                ...feature,
                                class_name: generate_forecast_node_class(id, step)
                            }
                        }
                        return set_feature_style(feature)
                    },
                    onEachFeature: (feature, layer) => {
                        if (feature.geometry.type !== 'Point') return
                        set_click_event.call(
                            this, 
                            layer, 
                            {
                                product,
                                feature,
                                language: this.language,
                            }
                        )
                    },
                    pointToLayer: (feature, latlng) => {
                        /* feautreGroup 才能支援 popup，layerGroup 不支援 */
                        const is_last_actual_path_node = feature.animation_step === 1
                        const is_forecast_node = feature.animation_step && feature.animation_step !== 1

                        const layer_group = L.featureGroup() 

                        if (feature.new_day) {
                            feature['marker-size'] = 'large';
                            layer_group.addLayer(make_typhoon_time_marker({id, feature, latlng}));
                        }
                        
                        if (this.is_newest_time || is_last_actual_path_node) {
                            const step = feature.animation_step
                            if (step) {
                                this.ty_timer = update_ty_animation_timer_structure(this.ty_timer, id, step)
                                feature = {
                                    ...feature,
                                    class_name: generate_forecast_node_class(id, step)
                                }

                                if (!this.typhoon_forecast) this.typhoon_forecast = true // 颱風預測小工具
                            }

                            if (feature.properties.is_show_point_datatime) {
                                layer_group.addLayer(make_typhoon_time_marker({id, feature, latlng}));
                            }
                        }
                        
                        if (this.is_newest_time || !is_forecast_node) { 
                            const my_icon = get_icon_marker_options(feature)
                            layer_group.addLayer(L.marker(latlng, {icon: my_icon, pane: id, zIndexOffset: 1000}));
                        }

                        if (feature.properties.last_data) {
                            layer_group.addLayer(make_typhoon_name_tag({id, feature, latlng, language: this.language}));
                        }
                        
                        return layer_group
                    },
                    callback: () => {
                        show_or_hide_typhoon_time_display(this.is_show_typhoon_time)
                        this.start_or_stop_typhoon_animation(this.is_show_typhoon_animation)
                        this.is_loaded_typhoon_message = true;
                        this.clear_geojson_popup_data()
                    },
                    destroy: () => {
                        this._clear_typhoon_animation_interval()
                        this.clear_geojson_popup_data()
                    },
                })
            },

            add_geojson_lightning(product, data_file_index) {
                const { id } = product
                const markers = []
                this.kmap.create_layer({
                    ...this._get_common_create_geojson_type_options(product, data_file_index), 
                    pane_type: 'top',
                    render_hint: {
                        draw_canvas: true
                    },
                    pointToLayer: (feature, latlng) => {
                        markers.push({
                            latlng: latlng,
                            type: feature['marker-symbol'],
                            color: feature['marker-color']
                        })

                        const circle = new L.Circle(latlng, {
                            weight: 6,
                            color: 'transparent',
                            pane: id
                        });

                        set_click_event.call(
                            this, 
                            circle, 
                            {
                                product,
                                feature,
                                language: this.language,
                            }
                        )

                        return circle
                    },
                    onLayerDidMount() {
                        return { data: markers }
                    },
                    onDrawLayer(info, data) {
                        const ctx = info.canvas.getContext('2d')
                        ctx.clearRect(0, 0, info.canvas.width, info.canvas.height)

                        const size = 6
                        data.forEach(datum => {
                            const d = [datum.latlng.lat, datum.latlng.lng]
                            if (info.bounds.contains([d[0], d[1]])) {
                                const dot = info.layer._map.latLngToContainerPoint([d[0], d[1]])

                                if (datum.color) {
                                    ctx.strokeStyle = datum.color
                                }

                                ctx.beginPath()
                                if (datum.type === 'cloud') {
                                    //畫十字
                                    ctx.moveTo(dot.x - size / 2, dot.y)
                                    ctx.lineTo(dot.x + size / 2, dot.y)
                                    ctx.moveTo(dot.x, dot.y - size / 2)
                                    ctx.lineTo(dot.x, dot.y + size / 2)
                                } else if (datum.type === 'pos') {
                                    //畫閃電
                                    ctx.moveTo(dot.x, dot.y - size)
                                    ctx.lineTo(dot.x - size, dot.y)
                                    ctx.lineTo(dot.x + size, dot.y)
                                    ctx.lineTo(dot.x, dot.y + size)
                                }
                                ctx.stroke()
                            }
                        })
                    },
                    callback: (options, pane) => {
                        pane.$el.style.pointerEvents = "none";
                        this.is_loaded_lightning_message = true;
                        this.clear_geojson_popup_data();
                    },
                    destroy: () => {
                        this.clear_geojson_popup_data();
                    }
                })
            },

            add_geojson_plan(product, data_file_index) {
                this._call_fit_bounds(product)

                const { id } = product
                this.kmap.create_layer({
                    ...this._get_common_create_geojson_type_options(product, data_file_index), 
                    pane_type: 'top',
                    pointToLayer: (feature, latlng) => {
                        const layer_group = L.featureGroup()
                        
                        const my_icon = get_icon_marker_options(feature);
                        const marker = L.marker(latlng, {icon: my_icon, pane: id});
                        layer_group.addLayer(marker)

                        if (feature['marker-text']) {
                            const label_text = L.divIcon({
                                className: "marker_icon_text",
                                html: `
                                    <span 
                                        style='
                                            color:${feature['text-color'] || 'yellow'};
                                            position: absolute;
                                            transform: translateX(-33%);
                                        '
                                    >
                                    ${feature['marker-text']}
                                    </span>
                                `
                            });
                            layer_group.addLayer(L.marker(latlng, {
                                icon: label_text,
                                pane: id,
                            }))
                        }
                        return layer_group
                    },
                })
            },
            
            add_geojson_airplane_wind(product, data_file_index) {
                const { id } = product
                this.kmap.create_layer({
                    ...this._get_common_create_geojson_type_options(product, data_file_index), 
                    pane_type: 'top',
                    pointToLayer: (feature, latlng) => {
                        const my_icon = get_icon_marker_options({
                            'marker-icon': 'flight',
                            'marker-color': 'blue'
                        });
                        
                        const marker = L.marker(latlng, {icon: my_icon, pane: id});
                        marker.on('click', () => {
                            this.is_show_chart_dialog = true
                            this.chart_type = 'airplane_wind'
                            this.airplane_wind_chart_options = feature.properties
                        })

                        return marker
                    },
                })
            },

            add_geojson_geomap(product) {
                const { id } = product
                const FIXED_GEOJSON_GEOMAP_FILE_INDEX = 0
                this.kmap.create_layer({
                    ...this._get_common_create_geojson_type_options(product, FIXED_GEOJSON_GEOMAP_FILE_INDEX), 
                    pane_type: 'top',
                    pointToLayer: (feature, latlng) => {
                        const my_icon = get_icon_marker_options(feature)
                        const marker = L.marker(latlng, {icon: my_icon, pane: id});
                        const tooltip_data = create_bind_marker_data_by_config(product, feature.properties, this.config)
                        const tooltip_html = (tooltip_data) ? _convert_marker_data_to_html(tooltip_data) : '';
                        tooltip_html && marker.bindTooltip(tooltip_html)

                        // 在 pointToLayer 中不能使用 await 所以只能在外部程式中 call await 了
                        const img_id = feature['properties']['Id']

                        const options = { 
                            product,
                            img_id, 
                            feature, 
                            client_name: this.client_name,
                            click_callback: assign_description.bind(this),
                        }

                        set_product_geomap_images_click_event(marker, options)
                        return marker
                    }
                })
            },
            
            _get_common_create_geojson_type_options(product, data_file_index) {
                const file_path = get_file_path(product, data_file_index)
                const not_to_show_opacity_tool_list = ['id_75', 'id_76'] // 根據客戶要求增減，或是未來討論由後端給參數
                return {
                    ...get_common_create_layer_options(product),
                    type: 'geojson',
                    url: file_path,
                    opacity_tool: !not_to_show_opacity_tool_list.includes(product['id'])
                }
            },

            _get_common_create_geojson_alert_options(id) {
                return {
                    pane_tag_config: {
                        no_data: 'pink'
                    },
                    pointToLayer: (feature) => {
                        this._add_alert_table_content(feature, id)
                    },
                    destroy: () => {
                        this.alerts[id] = {}
                    }
                }
            },

            _get_common_geojson_bind_popup_description_options(product) {
                return {
                    onEachFeature: (feature, layer) => {
                        // 機場閃電警示部分不需要綁定事件
                        if (product.product_sub_type === 'lightning_alert') {
                            if(feature.properties.description || feature.properties.table_content){
                                set_click_event.call(
                                    this, 
                                    layer, 
                                    {
                                        product,
                                        feature,
                                        language: this.language,
                                    }
                                )
                            }
                        } else {
                            set_click_event.call(
                                this, 
                                layer, 
                                {
                                    product,
                                    feature,
                                    language: this.language,
                                }
                            )
                        }
                       
                        
                    },
                    destroy: () => {
                        this.clear_geojson_popup_data()
                    },
                    callback: () => {
                        this.clear_geojson_popup_data()
                    }
                }
            },

            _get_common_geojson_set_style_options(product) {
                const { color_line } = product
                return {
                    style: (feature) => {
                        if (!feature.color_line && color_line) {
                            feature.color_line = color_line
                        }
                        if (feature?.properties?.distance) {
                            feature.class_name = `${feature.class_name ? `${feature.class_name} alert_line`: 'alert_line'}`;
                        }
                        return set_feature_style(feature);
                    },
                }
            },

            // 原本的 gis
            add_gis_station(product, data_file_index) {
                this.kmap.create_layer(this._get_common_create_gis_type_options(product, data_file_index, 'station'),)
            },

            add_gis_auto(product, data_file_index) {
                this.kmap.create_layer(this._get_common_create_gis_type_options(product, data_file_index, 'auto'),)
            },

            _nineblock_render(product, timestamp) {
                const { files, id } = product
                const has_current_time_file = files.some(({ data_time_timestamp }) => data_time_timestamp === timestamp)
                if (!has_current_time_file) {
                    // 當下時間點沒有資料的狀況
                    this.$refs.kxdmap.panes_config[id].is_on_map = false
                    this.$refs.kxdmap.clear_pane(id)
                }else if (!this.$refs.kxdmap.panes_config[id].is_on_map) {
                    // 沒資料的狀況下，選擇有資料的時間點後，因為使用 change 繪製會出錯，所以進行刪除重繪
                    this.$refs.kxdmap.delete_pane(id)
                    this.add_other_product(product)
                } else {
                    // 切換資料
                    this.$refs.kxdmap.panes_config[id].is_on_map = true
                    const current_file = files.find(({ data_time_timestamp }) => data_time_timestamp === timestamp)
                    this.$refs.kxdmap.clear_pane(id) // 為了修正自動雨量站在切換資料時間後會有新舊資料疊加情況而加這句
                    this.kmap.panes[id]?.nb_render?.change(current_file.file_path)
                }
            },
			
            add_nineblock_gauge(product, data_file_index) {
                const file_path = get_file_path(product, data_file_index)
                const sub_type = 'gauge'

                const { id } = product
                const is_pane_exist = this.$refs.kxdmap.panes[id]

                // 為了修正地圖自動雨量站換時間會畫不出圖的問題，所以把這句註解(暫不確定是否會造成其他問題)
                // if (is_pane_exist) return this._nineblock_render(product, this.current_timestamp)

                this.kmap.create_layer({
                    ...get_common_create_layer_options(product),
                    type: 'nineblock',
                    pane_type: 'top',
                    url: file_path,
                    is_on_map: true,
                    render_hint: {
                        sub_type,
                        default_color: '#00c',
                        column_map: {
                            ID: 'STID',
                            name: this.language === 'en' ? 'STNM_EN' : 'STNM',
                            compare: 'MIN_10',
                            lat: 'LAT',
                            lng: 'LON',
                            val_map: [null, 'MIN_10', null, null, null, null, null, null],
                        },
                        emit_popup_callback: emit_station_data.call(this, 'NB-', sub_type),
                        pointToLayerCallback: (feature) => {
                            // 切時間軸資料會變
                            const is_popup_table_open = Object.keys(this.gis_popup_data).length
                            if(is_popup_table_open) {
                                const origin_coordinates = JSON.stringify(this.gis_popup_data.feature.geometry.coordinates)
                                const new_coordinates = JSON.stringify(feature.geometry.coordinates)
                                if (new_coordinates === origin_coordinates) {
                                    this.gis_popup_data.feature = feature
                                }
                            }
                        },
                    },
                    callback: () => {
                        this.is_loaded_gauge_message = true
                    }
                })
            },

            // 畫投落送資料
            add_gis_dropsonde(product, data_file_index) {
                const { id } = product
                this.set_dropsonde({
                    id
                })
                const file_path = get_file_path(product, data_file_index)
                const sub_type = 'dropsonde'
                this.kmap.create_layer({
                    ...get_common_create_layer_options(product),
                    type: 'gis',
                    pane_type: 'top',
                    url: file_path,
                    render_hint: {
                        sub_type,
                        wmarker_map: {
                            spd: 'wind_speed',
                            dir: 'wind_direction',
                            val_map: [null, null, null, null, null, null, null, null],
                        },
                        judge_whether_wmarker_display: (feature) => {
                            const point_hPa = feature.geometry.coordinates[2]
                            const color = {
                                925: '#FF0000',
                                850: '#FE1B1B',
                                700: '#FD3535',
                                500: '#FC5050',
                                400: '#FB6A6A',
                                300: '#FA8585',
                                200: '#F8A0A0'
                            }

                            feature['color_line'] = (color[point_hPa]) ? color[point_hPa] : '#ff0000';
                            feature['marker-color'] = (color[point_hPa]) ? color[point_hPa] : '#ff0000';

                            return (this.dropsonde.hPa === 'all') ? true : this.dropsonde.hPa === point_hPa ;
                        },
                        emit_popup_callback: emit_dropsonde_data.call(this, 'WM', sub_type),
                    },
                })
            },

            add_gis_flight(product, data_file_index) {
                this._call_fit_bounds(product)
                const file_path = get_file_path(product, data_file_index)
                const is_popup_table_open = Object.keys(this.gis_popup_data).length
                const sub_type = 'flight'
                this.kmap.create_layer({
                    ...get_common_create_layer_options(product),
                    type: 'gis',
                    pane_type: 'top',
                    url: file_path,
                    render_hint: {
                        sub_type,
                        wmarker_map: {
                            spd: 'WSPD',
                            dir: 'WDIR',
                            val_map: [null, null, null, null, null, null, null, null],
                        },
                        emit_popup_callback: emit_station_data.call(this, 'WM', sub_type),
                    },
                    pointToLayer: (feature) => {
                        // 切時間軸資料會變
                        if(is_popup_table_open) {
                            const origin_coordinates = JSON.stringify(this.gis_popup_data.feature.geometry.coordinates)
                            const new_coordinates = JSON.stringify(feature.geometry.coordinates)
                            if (new_coordinates === origin_coordinates) {
                                this.gis_popup_data.feature = feature
                            }
                        }
                    },
                    destroy: () => {
                        this.clear_gis_popup_data();
                    }
                })
            },

            _get_common_create_gis_type_options(product, data_file_index, sub_type) {
                const file_path = get_file_path(product, data_file_index)
                const is_popup_table_open = Object.keys(this.gis_popup_data).length !== 0

                return {
                    ...get_common_create_layer_options(product),
                    type: 'gis',
                    pane_type: 'top',
                    url: file_path,
                    render_hint: {
                        sub_type,
                        wmarker_map: {
                            id: 'STID',
                            name: 'STNM',
                            spd: 'WDSDKT',
                            dir: 'WDIR',
                            val_map: [null, null, null, 'HUMD', null, 'TEMP', null, 'PRES']
                        },
                        emit_popup_callback: emit_station_data.call(this, 'WM', sub_type),
                    },
                    pointToLayer: (feature, latlng) => {
                        // 切時間軸資料會變
                        if(is_popup_table_open) {
                            const origin_station_code = this.gis_popup_data.feature.api_data.station_code
                            if (feature.api_data.station_code === origin_station_code) {
                                this.gis_popup_data.feature = feature
                            }
                        }

                        if (sub_type === 'station' && !feature.properties.WDIR) {
                            const layer_group = L.featureGroup();
                            const label_text = L.divIcon({
                                className: "station-icon gis-marker",
                                html: `
                                    <div class="x-m-i5"
                                        style="position: relative; z-index: 1;"
                                    >
                                        <div 
                                            class="x-m-wind-point"
                                            style="background-color: #2c7da8;"
                                        ></div>
                                    </div>
                                `
                            });
                            layer_group.addLayer(L.marker(latlng, {
                                icon: label_text,
                                pane: product.id,
                            }))
                            return layer_group
                        }
                    },
                    callback: () => {
                        this.is_loaded_station_message = true
                    },
                    destroy: () => {
                        this.clear_gis_popup_data();
                    }
                }
            },

            _get_marker_icon_text({ marker_text, color, addional_class }) {
                return L.divIcon({
                    className: `marker_icon_text ${addional_class}`,
                    html: `
                        <span 
                            style='
                                color:${color || 'yellow'};
                                position: absolute;
                                transform: translateX(calc(-50% + 0.4rem));
                            '
                        >
                        ${marker_text}
                        </span>
                    `
                });
            },

            clear_gis_popup_data() {
                this.gis_popup_data = {};
                this.gis_popup_type = '';
                this.description_info = {}
            },

            clear_geojson_popup_data() {
                this.description_info = {}
            },

            // geojson alert
            _add_alert_table_content(feature, id) {
                const alert_content = create_alert_content_structure(feature, id)
                if (!alert_content) return

                if (!this.alerts[id]) {
                    this.alerts = {
                        ...this.alerts, 
                        [id]: {}
                    }
                }

                if (!this.alerts[id][this.current_timestamp]) {
                    this.alerts = {
                        ...this.alerts,
                        [id]: {
                            ...this.alerts[id],
                            [this.current_timestamp]: []
                        }
                    }
                }

                // 有一個調查不出原因的會重複添加 alert content 的狀況，所以用這個方式處理
                const check_alert_content_duplicated = (alert_content) => {
                    return this.alerts[id][this.current_timestamp].find(exist_content => {
                        return JSON.stringify(exist_content) === JSON.stringify(alert_content)
                    })
                }

                if (check_alert_content_duplicated(alert_content)) return

                this.alerts[id][this.current_timestamp] = [
                    ...this.alerts[id][this.current_timestamp],
                    alert_content
                ]
            },
            
            _add_feature_reference(source_feature, product, callback_options = {}) {
                const { id, pane_text } = product
                if (source_feature.reference) {
                    const reference_url = source_feature.reference[0];
                    const default_onEachFeature = (useless_feature, layer) => {
                        set_click_event.call(
                            this, 
                            layer, 
                            {
                                product,
                                feature: source_feature,
                                language: this.language,
                            }
                        )
                    }
                    const { onEachFeature = default_onEachFeature } = callback_options 
                    this.kmap.create_layer({
                        pane: `${id}`,
                        name: `${id}_${reference_url}`,
                        text: pane_text,
                        type: 'geojson',
                        url: reference_url,
                        style: () => set_feature_style(source_feature),
                        onEachFeature,
                        render_hint: {
                            save_layer_data: false
                        },
                        callback: () => {
                            this.clear_geojson_popup_data();
                        }
                    })
                }
            },

            // geojson typhoon

            // 調整地圖視窗，目前是颱風特有的功能
            _call_fit_bounds(product) {
                const { id, product_bounds } = product

                // 只有第一次選到該產品才 call function，
                // 避免自動更新或是時間軸跳動還繼續調整視窗
                if(this.fit_bounds_record.has(id)) return

                this.fit_bounds_record.add(id)

                if (this.is_mobile_width) {
                    return this.$refs.kxdmap.set_map_view({
                        latlng: this.map_config.options.center, 
                        zoom: this.map_config.options.minZoom
                    })
                } 
                
                return this.$refs.kxdmap.fit_bounds(product_bounds)
            },

            toggle_typhoon_animation() {
                this.set_is_show_typhoon_animation(!this.is_show_typhoon_animation)
            },

            update_typhoon_loop_duration() {
                //如果正在播放的話, 要重啟timer
                if (this.is_show_typhoon_animation && this.is_newest_time){
                    this._clear_typhoon_animation_interval()
                    this._make_typhoon_animation()
                }
            },

            start_or_stop_typhoon_animation(is_show_typhoon_animation) {
                if (is_show_typhoon_animation && this.is_newest_time) {
                    return this._make_typhoon_animation()
                }

                return this._clear_typhoon_animation_interval(this.is_newest_time)
            },

            set_typhoon_forecast_step(method) {
                Object.keys(this.ty_timer).forEach(id => {
                    if(this.ty_timer[id].timer) this.toggle_typhoon_animation();
                    const current_step = this.typhoon_forecast_step;
                    
                    if (method === 'next') {
                        this.typhoon_forecast_step = current_step + 1;
                    }
                    else if (method === 'prev') {
                        this.typhoon_forecast_step = current_step - 1;
                    }

                    if (this.typhoon_forecast_step > this.ty_timer[id].max_step) {
                        this.typhoon_forecast_step = 0;
                    }
                    else if (this.typhoon_forecast_step < 0) {
                        this.typhoon_forecast_step = this.ty_timer[id].max_step;
                    }

                    this._control_typhoon_datetime_dom(id, this.typhoon_forecast_step - 1)
                    this.ty_timer[id].current_step = this.typhoon_forecast_step

                });
            },

            _make_typhoon_animation() {
                Object.keys(this.ty_timer).forEach(id => {
                    if(!this.ty_timer[id].timer) {
                        this.ty_timer[id].timer = setInterval(() => {
                            if (this.typhoon_forecast_step > this.ty_timer[id].max_step) {
                                this.typhoon_forecast_step = 0;
                            }

                            this._control_typhoon_datetime_dom(id, this.typhoon_forecast_step)
                            this.ty_timer[id].current_step = this.typhoon_forecast_step
                            this.typhoon_forecast_step++;
                        }, this.typhoon_loop_duration);
                    }
                });
            },

            _control_typhoon_datetime_dom(id, step) {
                document.querySelectorAll(`.${id}_loop`).forEach(element => {
                    element.classList.add('typhoon--forecast')
                })
                document.querySelectorAll(`.${id}_loop.loop_step_${step}`).forEach(element => {
                    element.classList.remove('typhoon--forecast')
                })
            },

            _clear_typhoon_animation_interval() {
                Object.entries(this.ty_timer).forEach(([id, { timer, current_step }]) => {
                    clearInterval(timer)
                    this.ty_timer[id].timer = undefined
                    this.is_newest_time && this._control_typhoon_datetime_dom(id, current_step)
                });
            },

            // gis
            show_wmarker_chart_dialog() {
                this.chart_options = this.gis_popup_data.feature.api_data ?? {}
                this.is_show_chart_dialog = true
                this.chart_type = 'wmarker'
            },

            close_chart_dialog() {
                this.is_show_chart_dialog = false
                this.chart_type = ''
                setTimeout(() => {
                    this.chart_options = {}
                    this.skew_options = {}
                    this.airplane_wind_chart_options = {}
                }, 800)
            },

            // 底圖
            add_geomap_products() {
                this.selected_geomap_products.forEach(product => {
                    if(product['product_sub_type'] === 'freeway_live_traffic') {
                        return this._add_background_live_traffic(product)
                    }

                    this.add_background_geojson(product)
                })
            },

            add_background_geojson(product) {
                const { id, config } = product
                
                if (id === 'id_62_95') { // 底圖-主要河川
                    this.kmap.create_layer({
                        render_hint: { canvas_pane: true },
                        ...this._create_common_create_background_options(product),
                        ...this._get_common_geojson_set_style_options(product),
                    })
                    return
                }
                
                this.kmap.create_layer({
                    ...this._create_common_create_background_options(product),
                    ...this._get_common_geojson_set_style_options(product),
                    onEachFeature: (feature, layer) => {
                        set_layer_popup_descriptions(layer, feature, this.language);
                    },
                    pointToLayer: (feature, latlng) => {
                        this._add_feature_reference(feature, product) 

                        const layer_group = L.featureGroup()
                        if (feature['marker-text']) {
                            const label_text = L.divIcon({
                                className: "marker_icon_text",
                                html: `
                                    <span 
                                        style='
                                            color:${feature['text-color'] || 'yellow'};
                                            position: absolute;
                                            transform: translateX(-33%);
                                        '
                                    >
                                    ${feature['marker-text']}
                                    </span>
                                `
                            });
                            layer_group.addLayer(L.marker(latlng, {
                                icon: label_text,
                                pane: id,
                            }))
                        }

                        const my_icon = get_icon_marker_options(feature)
                        let marker = L.marker(latlng, {icon: my_icon, pane: id});
                    
                        // key 是用來取 property 的 key ，text 是顯示在畫面上的中文
                        let bind_html = '';
                        Object.entries(config).forEach(([key, text]) => {
                            const value = feature.properties[key];
                            if (value) {
                                if (text){
                                    bind_html = `${bind_html}<div>${text}: ${value}<div>`;
                                }else{
                                    bind_html = `${bind_html}<div>${value}<div>`;
                                }
                            }
                        });

                        bind_html && marker.bindTooltip(bind_html)
                        // 點擊航空站到中心功能
                        marker.on('click', ()=> {
                            const current_zoom = Number(this.$refs.kxdmap.$refs['kxd-map'].getAttribute('zoom'));
                            setTimeout(() => {
                                this.kmap.set_map_view({
                                    latlng: [latlng.lat, latlng.lng], 
                                    zoom: current_zoom + 1 // 這不會超過 maxZoom 設定
                                })
                            }, 50);
                        })

                        layer_group.addLayer(marker)

                        return layer_group
                    }
                })
            },

            async _add_background_live_traffic(product) {
                const { id, api_path } = product

                const DIRECION_MAPPING = {
                    N: {text: '北向'},
                    E: {text: '東向'},
                    W: {text: '西向'},
                    S: {text: '南向'},
                }

                const WEIGHT_MAPPING = {
                    6: 2, 7: 2, 8: 2, 9: 3, 10: 5,
                    11: 8, 12: 15, 13: 30, 14: 70,
                }

                const _modify_line_weight = (map, layer) => {
                    return () => {
                        const current_zoom = map.getZoom();
                        layer.setStyle({weight: WEIGHT_MAPPING[current_zoom]});
                    }
                }

                if(!this.geomap_live_data[id]['data']) {
                    const with_client_name_api_path = `${this.client_name}/${api_path}`
                    await this._assign_geomap_live_data(id, with_client_name_api_path)
                }

                let layer
                this.kmap.create_layer({
                    ...this._create_common_create_background_options(product),
                    style: (feature) => {
                        const { SectionID: section_id, } = feature.properties
                        const color = this.geomap_live_data[id]['data'][section_id]?.color
                        return {
                            color: color || 'grey',
                            weight: 2,
                            lineCap: 'square',
                        }
                    },
                    onEachFeature: (feature, layer) => {
                        const { 
                            SectionID: section_id, 
                            SectionName: section_name, 
                            RoadDirection: road_direction 
                        } = feature.properties
                        const speed = this.geomap_live_data[id]['data'][section_id]?.speed
                        const time = this.geomap_live_data[id]['data'][section_id]?.time
                        const tooltip_html = `
                            路段: ${section_name}<br>
                            方向: ${DIRECION_MAPPING[road_direction]['text']}<br>
                            即時車速: ${speed ? speed : '未取得資訊'}<br>
                            旅行時間: ${time ? time : '未取得資訊'}
                        `
                        layer.bindPopup(tooltip_html)
                    },
                    callback: (options, pane, map) => {
                        pane.$el.style.pointerEvents = "none"
                        layer = Object.values(pane._layers)[0]
                        map.on('zoomend', _modify_line_weight(map, layer))
                        
                    },
                    destroy: (map) => {
                        map.off('zoomend', _modify_line_weight)
                    },
                })
            },

            async _assign_geomap_live_data(id, api_path) {
                const {
                    update_time, 
                    road_info_mapping
                } = await get_live_traffic_data(api_path, this.geomap_live_data[id]['update_time']) 
                if (road_info_mapping) {
                    this.geomap_live_data[id]['update_time'] = update_time
                    this.geomap_live_data[id]['data'] = road_info_mapping
                    this.traffic_live_display_time = transform_datetime_to_string(new Date(update_time))
                }
            },

            _create_common_create_background_options(product) {
                const { id, file_path, pane_order } = product
                return {
                    name: id,
                    url: file_path,
                    type: 'geojson',
                    pane_order: pane_order,
                    pane_type: 'top',
                    order_tool: false,
                    opacity_tool: false,
                    pane_tag_tool: false,
                }
            },

            add_table_geojson(product) {
                this.all_table_sign_latlng = {}
                const { id, alert_content } = product
                const get_item_color = item_num => alert_content[item_num]?.color ?? 'gray'
                this.kmap.create_layer({
                    ...this._create_common_create_background_options(product),
                    style: (feature) => {
                        const { item_num } = feature.properties
                        const color = get_item_color(item_num)
                        
                        return {
                            color
                        };
                    },
                    pointToLayer: (feature, latlng) => {
                        const { item_num } = feature.properties
                        const color = get_item_color(item_num)

                        this.all_table_sign_latlng = {
                            ...this.all_table_sign_latlng,
                            ...record_point_latlng(item_num, latlng)
                        }

                        const my_icon = get_icon_marker_options({
                            'marker-icon': 'circle',
                            'marker-color': color,
                            'marker-font-color': color,
                            'marker-border': color,
                            'class_name': `sign_${item_num}`,
                        })
                        let marker = L.marker(latlng, {icon: my_icon, pane: id});
                        bind_table_sign_tooltip(marker, alert_content, item_num)
                        marker.on('click', () => this.set_focal_sign_number(item_num))
                        return marker
                    }
                })
            },

            fill_data() {
                // 目的: 當該時間無資料時，取最近上一筆資料來替補該時間資料
                let filled_timestamp_list = [] //紀錄有被填補的時間

                this.keep_group_list.forEach(keep_item => { // 保留名單依序處理
                    let selected_id_list = this.selected_products.map(x => x.id);

                    if (selected_id_list.includes(keep_item.main_product)){ // 如果所選產品有在保留名單的main_product，則進行資料填補
                        // 1. 各產品依序處理
                        this.selected_products.forEach(product => {
                            let new_files = []; // 用來放新的 product.files

                            // 2. 時間軸上各時間依序處理
                            for (let i = 0; i < this.timebar_option.list.length; i++) {

                                // 3. 特定產品所有時間資料
                                for (let j = 0; j < product.files.length; j++) {
                                    
                                    // Situation 1, 有特定時間資料 => 該時間資料暫存到 new_files 裡
                                    if (this.timebar_option.list[i] == product.files[j].data_time_timestamp) {
                                        new_files.push(product.files[j]);
                                        i++;
                                    }

                                    // Situation 2, 沒有特定時間資料 && 此時間小於files資料中的最小時間 => 跳過不需處理

                                    // Situation 3, 沒有特定時間資料 && 此時間大於files資料中的最小時間 => 此特定時間的資料用上一筆時間資料替補
                                    else if (this.timebar_option.list[i] != product.files[j].data_time_timestamp && this.timebar_option.list[i] > product.files[0].data_time_timestamp) {
                                        if (i < product.files.length) {
                                            new_files.push({
                                                created_date_timestamp: this.timebar_option.list[i],
                                                data_time_timestamp: this.timebar_option.list[i],
                                                file_path: product.files[j - 1].file_path,
                                                modified_date_timestamp: this.timebar_option.list[i]
                                            });
                                            filled_timestamp_list.push(this.timebar_option.list[i])
                                            i++;
                                        }
                                    }
                                }

                                // 當後半部時間軸沒有
                                if (this.timebar_option.list[i] > product.files[product.files.length - 1].data_time_timestamp) {
                                    new_files.push({
                                        created_date_timestamp: this.timebar_option.list[i],
                                        data_time_timestamp: this.timebar_option.list[i],
                                        file_path: product.files[product.files.length - 1].file_path, // 最新時間的那筆資料
                                        modified_date_timestamp: this.timebar_option.list[i]
                                    });
                                    filled_timestamp_list.push(this.timebar_option.list[i])
                                }
                            }

                            product.files = new_files;
                            product.timestamp_list = new_files.map(x => x.data_time_timestamp);
                        });
                    }
                })
                
                this.filled_timestamp_list = [...new Set(filled_timestamp_list.sort())]
            },

            change_favorites_hint_text(text) {
                this.favorites_hint_text = text;
            },
        },
    }
</script>

<style lang="scss" scoped>
    .msg-container {
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: auto;
        width: 180px;
        height: 45px;
        border-radius: 8px;
        background-image: linear-gradient(to right, #2366f8 0%, #2cfde1 100%);
        z-index: 5000;

        .msg-mask {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
        }
    }

    .msg-container.empty {
        height: fit-content;
        padding: 2px 0;
        box-sizing: border-box;

        .text-container {
            flex-flow: column;
            height: auto;
        }
    }

    .text-container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 175px;
        height: 40px;
        background-color: #121624;
        border-radius: 8px;

        .text {
            margin: 0 .8rem;
            font-size: 12px;
            color: #fff;
            text-align: center;
        }
    }

    .diagram-dialog {
        position: fixed;
        bottom: -100%;
        left: 0;
        width: 100%;
        z-index: 3;
        transition: bottom .8s ease-out;
    }
</style>

<style lang="scss">
    @import '../../../css/emap/emap.css';
    @import '../../../css/mixin';

    .kxd-map[zoom='9'],
    .kxd-map[zoom='10'],
    .kxd-map[zoom='11'],
    .kxd-map[zoom='12'],
    .kxd-map[zoom='13'],
    .kxd-map[zoom='14'] {
        .gis-marker:hover {
            z-index: 2000 !important;
        }
        
        .station-icon.gis-marker {
            z-index: 2001 !important;
            pointer-events: none;
        }

        .x-marker-container:hover {
            font-size: 16px;
            z-index: 2;

            &::before {
                content: '';
                transform: translate(-50%, -50%);
                display: block;
                width: 7rem;
                height: 7rem;
                border: 2px solid #fff;
                backdrop-filter: blur(10px);
            }
        }
    }
    
    .kxd-map[zoom='1'],
    .kxd-map[zoom='2'],
    .kxd-map[zoom='3'],
    .kxd-map[zoom='4'],
    .kxd-map[zoom='5'],
    .kxd-map[zoom='6'],
    .kxd-map[zoom='7'],
    .kxd-map[zoom='8'] {
        .leaflet-zoom-hide .x-m- {
            &i9,&i7,&i4 {
                display: none;
            }
        }
    }

    .x-m- {
        &color-a {
            color: #ceffce;
        }

        &i9 {
            position: absolute;
            top: -24px;
            left: 28px;
            transform: translate(-50%, -50%);
        }

        &i8 {
            position: absolute;
            top: -24px;
            transform: translate(-50%, -50%);
            text-align: center;
        }

        &i7 {
            position: absolute;
            top: -24px;
            left: -28px;
            transform: translate(-50%, -50%);
        }

        &i6 {
            position: absolute;
            top: 0;
            left: 28px;
            transform: translate(-50%, -50%);
        }

        &i5 {
            position: absolute;
            top: -8px;
            left: -8px;
            width: 16px;
            height: 16px;
            text-align: center;
        }

        &i4 {
            position: absolute;
            top: 0;
            left: -28px;
            transform: translate(-50%, -50%);
        }

        &i3 {
            position: absolute;
            top: 24px;
            left: 28px;
            transform: translate(-50%, -50%);
        }

        &i2 {
            position: absolute;
            top: 24px;
            transform: translate(-50%, -50%);
        }

        &i1 {
            position: absolute;
            top: 24px;
            left: -28px;
            transform: translate(-50%, -50%);
        }

        &sta-name {
            position: absolute;
            top: 40px;
            transform: translate(-50%, -50%);
            width: max-content;
            color: var(--grey-pipe);
        }

        &nb-circle-a {
            position: absolute;
            border-radius: 10px;
        }
    }

    .point-tooltip {
        position: fixed;
        top: 0;
        z-index: 2;
    }

    .hide-maptool {
        display: none;
    }

    .bottom-dialog {
        position: absolute;
        bottom: 0;
        display: flex;
        align-items: flex-end;
        width: 100%;
        height: 0;
        z-index: 4003; // 此修改是為了壓住右邊那排的 map-tool
    }

    .bottom-tool {
        position: absolute;
        bottom: 0;
        display: flex;
        align-items: flex-end;
        width: 100%;
        height: 0;
        z-index: 4001; // 此修改是為了層級低於地圖設定視窗
    }

    .typhoon--forecast {
        display: none;
    }

    .leaflet-tooltip-pane {
        .monitor-table-tooltip {
            border-radius: .5rem;
            opacity: 1 !important;
        }
    }

    .table-sign {
        padding: .25rem;
        background: #fff;
        border-radius: .5rem;

        &__text {
            padding: .25rem;
        }
    }

    .typhoon-forecast-control {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: .5em;
        padding: 0 10px;
    }
    .typhoon-forecast-control .typhoon-spd-select{
        flex: 0;
    }
    .typhoon-forecast-control .typhoon-spd-select [for="typhoon-spd-select"]{
        color: #fff;
    }
    .typhoon-forecast-control  .theme--light.v-select .v-select__selections{
        color: #fff;
    }

    .typhoon-forecast-tooltip {
        position: absolute;
        top: 0;
        left: 50%;
        z-index: 1 !important;
        border-radius: 8px;
        overflow: hidden;
        background-color: var(--base-gray);
    }

    .typhoon-forecast-title {
        width: 100%;
        padding: 5px;
        color: #fff;
        background-color: var(--base-dark-blue);
        cursor: grab;
    }

    .typhoon-forecast-btn {
        display: flex;
        align-items: center;
        width: 2.3em;
        height: 2.3em;
        margin: 5px;
        background-color: #ff3970;
        background-color: var(--map-timebar-main-color);
        color: #fff;
        box-shadow: inset 0 0 0 0.2em #fff;
        border: 0.1em solid #ff3970;
        border: 0.1em solid var(--map-timebar-main-color);
        border-radius: 50%;
        transition: 0.5s;
        cursor: pointer;
    }

    .typhoon-forecast-btn-play:before {
        content: '▶';
        width: 100%;
        text-align: center;
    }

    .typhoon-forecast-btn-play.active:before {
        content: '❚❚';
    }

    .favorites-hint{
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: auto;
        width: 210px;
        height: 40px;
    }

    .marker_tooltip {
        background-color: transparent;
        border-color: transparent;
        opacity: 1;
        box-shadow: none;
    }
    .marker_tooltip:before {
        border-color: transparent;
    }
    .tooltip_id_899 {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #ffffffdd;
        border: 1px solid black; 
        border-radius: 3px; 
        padding: 4px;
    }
    .standby_area_id_899 {
        display: inline-block;
        margin-right: 3px;
    }

    .light_id_899 {
        display: inline-block; 
        width: 10px; 
        height: 10px; 
        margin: 0 2px;
        border-radius: 100px;
        border: 1px solid white;
    }
</style>